// Execution logic for the unified agent's two side-effecting primitives:
//   - apply_edits     : apply a PresentationModification batch (act)
//   - get_slides_info : re-extract layout JSON and/or capture screenshots (observe)
//
// Both operate against the turn-scoped AgentToolContext (FactSet intent bridge
// only). apply_edits reuses the same finalize + apply engine as the terminal
// fast-path, so the two paths have identical edit capability. get_slides_info
// re-runs the windowed extractor so the agent observes TRUE post-apply geometry
// (the turn-start extract goes stale after the first apply).

import { CHAT_CONTEXT_WINDOW_SIZE } from "../../../shared/config/constants";
import {
  PresentationContent,
  PresentationModification,
  SlideContent,
  ToolCall,
  ToolResult,
  ApplyResultSummary,
  ModificationCitation,
  ReferenceSlideImage,
  ShapeContent,
} from "../../types";
import {
  applyModifications,
  exportSlideAsImage,
  extractPresentationContent,
  extractWindowedPresentationContent,
  listSlides,
} from "../slideService";
import { insertSlideLibraryItemsViaFactSet } from "../../slideLibrary/ui/host/insertionService";
import {
  readPreslinkWorkbookTagFields,
  remapPreslinkBindingsForInsertedSlides,
  type PreslinkBindingWorkbook,
} from "../../slideLibrary/ui/host/preslinkBindingService";
import { resolveLibrarySlidePptxSources } from "./slideLibraryAgentTools";
import { finalizeModelModifications } from "./modificationFinalizer";
import { expandLogoCompanyNames } from "./logoCompanyRefs";
import type { AgentToolContext, ToolExecutionPolicy } from "./types";
import {
  uploadGoldenArtifactJson,
  uploadGoldenArtifactPptx,
} from "../goldenArtifactsService";
import { runGoldenCaptureStep } from "../../utils/goldenCapture";
import { isGoldenApplyEditsPptxCaptureEnabled } from "../../config";
import { beginInDeckSlideReplacement } from "../inFlightTabRegistry";
import {
  extractDeckLayoutCatalogViaIntents,
  extractDesignMetadataViaIntents,
  exportPresentationAsBase64ViaIntents,
  getPreslinkFilePathsViaIntents,
  fetchPreslinkFilesViaIntents,
  deleteSlidesViaIntents,
  getActivePresentationRefViaIntents,
  insertSlidesFromBase64WithSourceFormattingViaIntents,
  listSlidesViaIntents,
  writeFileFromBase64WithOutcomeViaIntents,
  type FactSetPresentationRef,
  type FactSetSlideInfo,
  type GetPreslinkFilePathsResult,
  type FetchPreslinkFilesResult,
} from "../factset/intentBridgeService";
import { shouldUseFactSetBridge } from "../presentationHost";
import type { PptxLangfuseRunContext } from "../../langfuseTrace";
import { postPptxLangfuseObservation } from "../../langfuseTrace";
import { summarizeForToolObservation } from "../../../shared/services/toolObservationFormat";
import * as JSZipModule from "jszip";
import * as XLSXModule from "xlsx";
import {
  base64ByteLength,
  buildPreslinkProcessPresentationRequest,
  postPreslinkProcessPresentationFile,
  PreslinkReauthRequiredError,
  type PostPreslinkProcessPresentationFileOptions,
  type PreslinkProcessEvent,
  type PreslinkProcessOutputLinkedFile,
  type PreslinkProcessPresentationRequest,
  type PreslinkProcessResult,
} from "./preslinkProcessApi";
import {
  EXCEL_MAX_FULL_PATH_LENGTH,
  isPreslinkWorkbookWriteCollision,
  PRESLINK_WORKBOOK_NAME_ATTEMPTS,
} from "./preslinkChartPreflight";
import { fitRefreshedValueAxes } from "./preslinkChartAxis";
import { isAbortError } from "../../../shared/utils/abortError";
import { redactLocalPathText } from "../../../shared/utils/localPathRedaction";
import { forceReconnectFactSetOAuth } from "../../../shared/factset/factsetReconnect";
import { getBrand } from "./brandRegistry";
import { finalizePreslinkRefreshLifecycle } from "./preslinkTurnOrchestrator";

/**
 * Temporary PresLink diagnostics hook. It accepts a lazy payload builder so
 * disabled logging never pays to assemble large slide or workbook summaries.
 */
function logPreslinkDebugToTerminal(_buildOptions: () => {
  hypothesisId: string;
  location: string;
  message: string;
  data?: Record<string, unknown>;
}): void {
  // Temporary debug emission disabled after successful post-fix verification.
  // Keep callsites lazy so disabled logging does not compute debug payloads.
}

// Higher perception limits for the agent than the legacy get_slide_images caps
// (4/5) — multi-slide consistency review needs to see more of the deck.
export const MAX_AGENT_SLIDE_IMAGES = 12;
export const MAX_AGENT_TOTAL_REFERENCE_IMAGES = 16;

export interface AgentToolRunOptions {
  presentationContent?: PresentationContent;
  agentToolContext?: AgentToolContext;
  toolPolicy?: ToolExecutionPolicy;
  langfuseContext?: PptxLangfuseRunContext;
  goldenSubdirectory?: string;
  onProgress?: (message: string) => void;
}

export interface AgentToolRunResult {
  result: ToolResult;
  referenceImages?: ReferenceSlideImage[];
}

/**
 * Format a PresLink progress line for the UI, appending the source Excel file
 * name when it is not already part of the message.
 */
function formatPreslinkProgressMessage(message: string, event: PreslinkProcessEvent): string {
  const normalizedMessage = message.trim() || event.message?.trim() || event.type;
  const excelFile = event.excel_file?.trim();
  if (!excelFile) return normalizedMessage;
  if (normalizedMessage.toLocaleLowerCase().includes(excelFile.toLocaleLowerCase())) {
    return normalizedMessage;
  }
  return `${normalizedMessage} (${excelFile})`;
}

/**
 * The process service swaps in new data but keeps ChartCreator's fixed axis
 * bounds, so a chart moved to another company draws clipped. Re-fits those
 * bounds in the processed workbooks, then runs one LLM-free pass so the service
 * redraws the slide pictures from them. Any failure keeps the original result,
 * because a clipped chart is better than a failed update.
 */
async function redrawWithFittedValueAxes(
  request: PreslinkProcessPresentationRequest,
  processed: PreslinkProcessResult,
  options: PostPreslinkProcessPresentationFileOptions,
  emitProgress: (message: string) => void,
  log: ((message: string) => void) | undefined,
): Promise<PreslinkProcessResult> {
  const processedFiles = processed.processedLinkedFiles ?? [];
  if (!processed.processedPresentationBase64 || processedFiles.length === 0) return processed;
  const sourceByLinkKey = new Map(request.data.linked_files.map((file) => [file.link_key, file]));
  let rescaledCount = 0;
  const fittedFiles = await Promise.all(
    processedFiles.map(async (file) => {
      const source = sourceByLinkKey.get(file.link_key);
      if (!source) return file;
      try {
        const fitted = await fitRefreshedValueAxes(source.zip_base64, file.zip_base64);
        rescaledCount += fitted.rescaled.length;
        return { ...file, zip_base64: fitted.workbookBase64 };
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        log?.(
          redactLocalPathText(
            `[apply_preslink] could not fit chart axes in ${file.file_name ?? file.link_key}: ${message}`
          )
        );
        return file;
      }
    })
  );
  if (rescaledCount === 0) return processed;

  emitProgress("PresLink: Fitting the chart axis to the new data.");
  try {
    const redrawn = await postPreslinkProcessPresentationFile(
      {
        ...request,
        skip_llm: true,
        data: {
          ...request.data,
          presentation: {
            ...request.data.presentation,
            zip_base64: processed.processedPresentationBase64,
          },
          linked_files: fittedFiles.map((file) => ({
            link_key: file.link_key,
            zip_base64: file.zip_base64,
            file_name: file.file_name ?? sourceByLinkKey.get(file.link_key)?.file_name ?? "",
          })),
        },
      },
      options
    );
    const redrawnKeys = new Set((redrawn.processedLinkedFiles ?? []).map((file) => file.link_key));
    const complete =
      redrawn.summary.errors.length === 0 &&
      Boolean(redrawn.processedPresentationBase64) &&
      redrawnKeys.size === processedFiles.length &&
      processedFiles.every((file) => redrawnKeys.has(file.link_key));
    if (!complete) {
      log?.("[apply_preslink] axis redraw pass returned an incomplete result; keeping the first pass.");
      return processed;
    }
    return { ...redrawn, summary: processed.summary };
  } catch (error) {
    if (isAbortError(error)) throw error;
    const message = error instanceof Error ? error.message : String(error);
    log?.(
      redactLocalPathText(`[apply_preslink] axis redraw pass failed; keeping the first pass: ${message}`)
    );
    return processed;
  }
}

async function observeToolSubstep<T>(params: {
  langfuseContext?: PptxLangfuseRunContext;
  name: string;
  input?: unknown;
  metadata?: Record<string, unknown>;
  run: () => Promise<T>;
  summarizeOutput?: (value: T) => unknown;
}): Promise<T> {
  const startedAtMs = Date.now();
  try {
    const value = await params.run();
    const endedAtMs = Date.now();
    if (params.langfuseContext) {
      postPptxLangfuseObservation(
        params.langfuseContext,
        {
          name: params.name,
          ...(params.input !== undefined
            ? { input: summarizeForToolObservation(params.input) }
            : {}),
          output: summarizeForToolObservation(params.summarizeOutput?.(value) ?? value),
          metadata: {
            ...params.metadata,
            durationMs: Math.max(0, endedAtMs - startedAtMs),
          },
          level: "DEFAULT",
          startTime: new Date(startedAtMs).toISOString(),
          endTime: new Date(endedAtMs).toISOString(),
        },
        { phase: "multi_hop_executor" }
      );
    }
    return value;
  } catch (error) {
    const endedAtMs = Date.now();
    if (params.langfuseContext) {
      postPptxLangfuseObservation(
        params.langfuseContext,
        {
          name: params.name,
          ...(params.input !== undefined
            ? { input: summarizeForToolObservation(params.input) }
            : {}),
          metadata: {
            ...params.metadata,
            durationMs: Math.max(0, endedAtMs - startedAtMs),
          },
          level: "ERROR",
          statusMessage: error instanceof Error ? error.message : String(error),
          startTime: new Date(startedAtMs).toISOString(),
          endTime: new Date(endedAtMs).toISOString(),
        },
        { phase: "multi_hop_executor" }
      );
    }
    throw error;
  }
}

/** Slide indices touched by a modification batch (for screenshot/re-extract scope). */
export function collectAffectedSlideIndices(modifications: PresentationModification): number[] {
  const indices = new Set<number>();
  for (const slide of modifications.slides || []) {
    if (typeof slide.index === "number" && Number.isFinite(slide.index) && slide.index >= 0) {
      indices.add(Math.floor(slide.index));
    }
  }
  return Array.from(indices).sort((a, b) => a - b);
}

/** Real host slide ids this batch modified (existing slides), for faithful refinement tracking. */
export function collectModifiedSlideIds(modifications: PresentationModification): string[] {
  const ids: string[] = [];
  for (const slide of modifications.slides || []) {
    if (slide.action === "modify" && typeof slide.id === "string" && slide.id.length > 0) {
      ids.push(slide.id);
    }
  }
  return ids;
}

export interface PreslinkSkippedSlideOperation {
  slideId: string;
  action: "delete" | "duplicate";
}

export const PRESLINK_NATIVE_EDIT_BLOCKED_MESSAGE =
  "Native edits are blocked because PresLink replaced slides but fresh managed-object verification failed.";

type PreslinkTurnGuardContext = Pick<
  AgentToolContext,
  "preslinkMutationFailedReason" | "preslinkRefreshFailed"
>;

/**
 * Return the fail-closed reason that prevents native edits in this turn.
 *
 * Both checks describe a mutation that already ran, never what the user meant:
 * PresLink intent belongs to the main agent, so the absence of an
 * `apply_preslink` call simply means no PresLink work was needed.
 */
export function getPreslinkTurnBlockReason(
  ctx: PreslinkTurnGuardContext | undefined
): string | undefined {
  if (!ctx) return undefined;
  if (ctx.preslinkMutationFailedReason) {
    return `PresLink refresh failed, so no native edits were applied: ${ctx.preslinkMutationFailedReason}`;
  }
  if (ctx.preslinkRefreshFailed === true) {
    return PRESLINK_NATIVE_EDIT_BLOCKED_MESSAGE;
  }
  return undefined;
}

/**
 * Enforce the post-refresh skip-only rule: drop any whole-slide structural op
 * against a PresLink-managed slide, and any modify/delete shape op that targets
 * a PresLink-managed shape (keyed by post-replacement slide id). This keeps
 * native edits from re-touching or removing objects a PresLink refresh just
 * produced. create ops are untouched.
 */
export function excludePreslinkManagedShapeMods(
  modifications: PresentationModification,
  excludedShapeIdsBySlideId: Map<string, Set<string>> | undefined
): {
  modifications: PresentationModification;
  skippedShapeIds: string[];
  skippedSlideOps: PreslinkSkippedSlideOperation[];
} {
  if (!excludedShapeIdsBySlideId || excludedShapeIdsBySlideId.size === 0) {
    return { modifications, skippedShapeIds: [], skippedSlideOps: [] };
  }
  const skippedShapeIds: string[] = [];
  const skippedSlideOps: PreslinkSkippedSlideOperation[] = [];
  let changed = false;
  const slides: PresentationModification["slides"] = [];
  for (const slide of modifications.slides || []) {
    const slideId = typeof slide.id === "string" ? slide.id : undefined;
    const excluded = slideId ? excludedShapeIdsBySlideId.get(slideId) : undefined;
    if (
      slideId &&
      (slide.action === "delete" || slide.action === "duplicate") &&
      excludedShapeIdsBySlideId.has(slideId)
    ) {
      skippedSlideOps.push({ slideId, action: slide.action });
      changed = true;
      continue;
    }
    if (!excluded || excluded.size === 0 || !Array.isArray(slide.shapes)) {
      slides.push(slide);
      continue;
    }
    const keptShapes = slide.shapes.filter((shape) => {
      const targetsExcludedShape =
        (shape.action === "modify" || shape.action === "delete") &&
        typeof shape.id === "string" &&
        excluded.has(shape.id);
      if (targetsExcludedShape) {
        skippedShapeIds.push(shape.id);
        return false;
      }
      return true;
    });
    if (keptShapes.length === slide.shapes.length) {
      slides.push(slide);
      continue;
    }
    changed = true;
    slides.push({ ...slide, shapes: keptShapes });
  }
  return {
    modifications: changed ? { ...modifications, slides } : modifications,
    skippedShapeIds,
    skippedSlideOps,
  };
}

function summarizeApplyResult(summary: ApplyResultSummary): Record<string, unknown> {
  return {
    requested: summary.requested,
    applied: summary.applied,
    skipped: {
      count: summary.skipped.count,
      byReason: summary.skipped.byReason,
      issues: summary.skipped.issues,
    },
    errors: {
      count: summary.errors.count,
      byReason: summary.errors.byReason,
      issues: summary.errors.issues,
    },
    classification: summary.classification,
  };
}

function buildDeclaredCitedValues(
  citations: ModificationCitation[] | undefined
): Record<string, string> {
  const citedValuesByUuid: Record<string, string> = {};
  for (const citation of citations || []) {
    if (citation && typeof citation.id === "string" && typeof citation.value === "string") {
      citedValuesByUuid[citation.id] = citation.value;
    }
  }
  return citedValuesByUuid;
}

type SlideNumberIndexRef = { index: number; slideNumber?: number };
type SlideIdIndexRef = SlideNumberIndexRef & { id: string };
type CurrentSlideRef = { id: string; index: number; slideNumber?: number };

function slideMatchesRequestedNumber(
  slide: SlideNumberIndexRef,
  requestedNumbers: Set<number>
): boolean {
  if (typeof slide.slideNumber === "number") {
    return requestedNumbers.has(Math.floor(slide.slideNumber));
  }
  return requestedNumbers.has(Math.floor(slide.index) + 1);
}

type ReExtractWindowSlide = { index: number; id?: string; slideNumber?: number };

interface ReExtractTarget {
  /** Displayed slide numbers requested by the model (get_slides_info). */
  slideNumbers?: number[];
  /**
   * Resolved slide refs (insert/duplicate auto-returns). Matched and supplemented
   * by id or deck index, which avoids the `slideNumber ?? index` ambiguity that
   * would otherwise drop created slides whose displayed number is unknown.
   */
  slides?: Array<{ index?: number; id?: string; slideNumber?: number }>;
}

function sortSlidesByIndex<T extends { index: number }>(slides: T[]): T[] {
  return [...slides].sort((a, b) => a.index - b.index);
}

// Scoped re-extract of specific deck slide INDICES via the same V1
// RunOfficeExtractor slide-range filter the windowed extract uses. Scoped
// requests bypass the shared window cache, so this never pollutes the
// turn-start context. Failures degrade to [] (logged) rather than throwing.
async function extractSlidesByIndices(
  indices: number[],
  ctx?: AgentToolContext
): Promise<ReExtractWindowSlide[]> {
  const uniqueIndices = Array.from(new Set(indices.map((i) => Math.floor(i)))).filter(
    (i) => i >= 0
  );
  if (uniqueIndices.length === 0) return [];
  const ranges = uniqueIndices.map((index) => ({ start: index, end: index }));
  try {
    const content = await extractPresentationContent(ctx?.log, {
      forceRefresh: true,
      presentationRef: ctx?.presentationRef ?? undefined,
      filters: { slides: { ranges } },
    });
    return content.slides as ReExtractWindowSlide[];
  } catch (error) {
    ctx?.log?.(
      `[get_slides_info] scoped supplement extract failed for indices [${uniqueIndices.join(",")}]: ${
        error instanceof Error ? error.message : String(error)
      }`
    );
    return [];
  }
}

// Re-extract slide JSON for the agent. The windowed extract (turn-start working
// set) is ALWAYS run first and the `undefined`/window branch is unchanged, so
// the shared auto-return callers and the windowed golden assertions are
// preserved. When specific slides are requested, any that fall OUTSIDE the
// window are supplemented via a scoped extract and merged - so get_slides_info
// and the insert/duplicate auto-returns can reach slides beyond the window.
async function reExtractSlides(
  target: ReExtractTarget | undefined,
  ctx?: AgentToolContext
): Promise<{ slides: unknown[] }> {
  const content = await extractWindowedPresentationContent(ctx?.log, {
    forceRefresh: true,
    windowSize: CHAT_CONTEXT_WINDOW_SIZE,
    presentationRef: ctx?.presentationRef ?? undefined,
  });
  const windowSlides = content.slides as ReExtractWindowSlide[];

  // No specific target -> return the whole loaded window (unchanged behavior).
  if (!target || (!target.slideNumbers?.length && !target.slides?.length)) {
    return { slides: windowSlides };
  }

  // Ref mode (auto-returns): match + supplement by id / deck index.
  if (target.slides?.length) {
    const wantedIds = new Set(
      target.slides
        .map((s) => s.id)
        .filter((v): v is string => typeof v === "string" && v.length > 0)
    );
    const wantedIndices = new Set(
      target.slides.map((s) => s.index).filter((v): v is number => typeof v === "number")
    );
    const inWindow = windowSlides.filter(
      (s) => (s.id != null && wantedIds.has(s.id)) || wantedIndices.has(Math.floor(s.index))
    );
    const coveredIds = new Set(inWindow.map((s) => s.id).filter((v): v is string => v != null));
    const coveredIndices = new Set(inWindow.map((s) => Math.floor(s.index)));
    const missingRefs = target.slides.filter(
      (s) =>
        !(s.id != null && coveredIds.has(s.id)) &&
        !(typeof s.index === "number" && coveredIndices.has(Math.floor(s.index)))
    );
    if (missingRefs.length === 0) return { slides: sortSlidesByIndex(inWindow) };

    const idsNeedingResolve = missingRefs
      .filter((s) => typeof s.index !== "number" && typeof s.id === "string")
      .map((s) => s.id as string);
    const refsById = await resolveCurrentSlideRefsById(idsNeedingResolve, ctx);
    const missingIndices = missingRefs
      .map((s) =>
        typeof s.index === "number" ? Math.floor(s.index) : refsById.get(s.id ?? "")?.index
      )
      .filter((i): i is number => typeof i === "number");
    const supplemental = await extractSlidesByIndices(missingIndices, ctx);
    return { slides: sortSlidesByIndex([...inWindow, ...supplemental]) };
  }

  // Number mode (get_slides_info): match + supplement by displayed slide number.
  const wanted = new Set((target.slideNumbers ?? []).map((n) => Math.floor(n)));
  const inWindow = windowSlides.filter((slide) => slideMatchesRequestedNumber(slide, wanted));
  const covered = new Set<number>();
  for (const slide of inWindow) {
    covered.add(
      typeof slide.slideNumber === "number"
        ? Math.floor(slide.slideNumber)
        : Math.floor(slide.index) + 1
    );
  }
  const missingNumbers = Array.from(wanted).filter((n) => !covered.has(n));
  if (missingNumbers.length === 0) return { slides: sortSlidesByIndex(inWindow) };

  const numberToIndex = await buildCurrentSlideNumberToIndexMap(ctx);
  const missingIndices = missingNumbers
    .map((n) => numberToIndex.get(n))
    .filter((i): i is number => typeof i === "number");
  const supplemental = await extractSlidesByIndices(missingIndices, ctx);
  return { slides: sortSlidesByIndex([...inWindow, ...supplemental]) };
}

async function listCurrentSlideRefs(ctx?: AgentToolContext): Promise<CurrentSlideRef[]> {
  try {
    const slides = (await listSlides({
      forceRefresh: true,
      presentationRef: ctx?.presentationRef ?? undefined,
    })) as SlideIdIndexRef[];
    return slides
      .filter((slide) => typeof slide.id === "string" && slide.id.length > 0)
      .map((slide) => ({
        id: slide.id,
        index: Math.floor(slide.index),
        ...(typeof slide.slideNumber === "number"
          ? { slideNumber: Math.floor(slide.slideNumber) }
          : undefined),
      }));
  } catch (error) {
    ctx?.log?.(
      `[agent tools] failed to resolve current slide list for image capture: ${
        error instanceof Error ? error.message : String(error)
      }`
    );
  }
  return [];
}

async function buildCurrentSlideNumberToIndexMap(
  ctx?: AgentToolContext
): Promise<Map<number, number>> {
  const numberToIndex = new Map<number, number>();
  for (const slide of await listCurrentSlideRefs(ctx)) {
    if (typeof slide.slideNumber === "number") {
      numberToIndex.set(slide.slideNumber, slide.index);
    }
  }
  return numberToIndex;
}

async function buildCurrentSlideIndexToRefMap(
  ctx?: AgentToolContext
): Promise<Map<number, CurrentSlideRef>> {
  const indexToRef = new Map<number, CurrentSlideRef>();
  for (const slide of await listCurrentSlideRefs(ctx)) {
    indexToRef.set(slide.index, slide);
  }
  return indexToRef;
}

async function buildCurrentSlideNumberToRefMap(
  ctx?: AgentToolContext
): Promise<Map<number, CurrentSlideRef>> {
  const numberToRef = new Map<number, CurrentSlideRef>();
  for (const slide of await listCurrentSlideRefs(ctx)) {
    if (typeof slide.slideNumber === "number") {
      numberToRef.set(slide.slideNumber, slide);
    }
  }
  return numberToRef;
}

function fallbackSlideImageId(prefix: string, value: number): string {
  return `${prefix}:${Math.floor(value)}`;
}

async function resolveCurrentSlideRefsById(
  slideIds: string[],
  ctx?: AgentToolContext
): Promise<Map<string, SlideIdIndexRef>> {
  const refsById = new Map<string, SlideIdIndexRef>();
  if (slideIds.length === 0) return refsById;

  try {
    const slides = (await listSlides({
      forceRefresh: true,
      presentationRef: ctx?.presentationRef ?? undefined,
    })) as SlideIdIndexRef[];
    const wanted = new Set(slideIds);
    for (const slide of slides) {
      if (!wanted.has(slide.id)) continue;
      refsById.set(slide.id, {
        id: slide.id,
        index: Math.floor(slide.index),
        ...(typeof slide.slideNumber === "number"
          ? { slideNumber: Math.floor(slide.slideNumber) }
          : undefined),
      });
    }
  } catch (error) {
    ctx?.log?.(
      `[insert_library_slide] failed to resolve inserted slide refs from current slide list: ${
        error instanceof Error ? error.message : String(error)
      }`
    );
  }

  return refsById;
}

function fallbackSlideNumberToIndex(slideNumber: number): number {
  return Math.max(0, Math.floor(slideNumber) - 1);
}

function normalizeLayoutName(value: string | undefined): string {
  return value?.trim().toLowerCase() ?? "";
}

/**
 * Apply the active brand's verified body layout only to inserts where the model
 * did not choose a layout. The catalog lookup keeps this fallback tied to the
 * live deck and avoids sending a template-specific name that the host cannot
 * resolve.
 */
async function applyBrandContentLayoutFallback(
  modifications: PresentationModification,
  options: AgentToolRunOptions
): Promise<PresentationModification> {
  const configuredLayoutName = getBrand(options.toolPolicy?.brandId)?.contentLayoutName?.trim();
  if (!configuredLayoutName) return modifications;

  const hasUnqualifiedInsert = (modifications.slides ?? []).some(
    (slide) => slide.action === "insert" && !slide.layoutName?.trim()
  );
  if (!hasUnqualifiedInsert) return modifications;

  const ctx = options.agentToolContext;
  try {
    const catalog = await extractDeckLayoutCatalogViaIntents(ctx?.log, {
      forceRefresh: true,
      presentationRef: ctx?.presentationRef ?? undefined,
    });
    const normalizedConfiguredName = normalizeLayoutName(configuredLayoutName);
    const matchedLayout = catalog.layouts.find(
      (layout) =>
        normalizeLayoutName(layout.layoutName) === normalizedConfiguredName ||
        normalizeLayoutName(layout.matchingName) === normalizedConfiguredName
    );
    const resolvedLayoutName =
      matchedLayout?.layoutName?.trim() || matchedLayout?.matchingName?.trim();
    if (!matchedLayout || !resolvedLayoutName) return modifications;

    return {
      ...modifications,
      slides: (modifications.slides ?? []).map((slide) => {
        if (slide.action !== "insert" || slide.layoutName?.trim()) {
          return slide;
        }
        return {
          ...slide,
          layoutName: resolvedLayoutName,
          ...(matchedLayout.slideMasterName?.trim()
            ? { slideMasterName: matchedLayout.slideMasterName.trim() }
            : undefined),
        };
      }),
    };
  } catch (error) {
    ctx?.log?.(
      `[apply_edits] unable to resolve branded content layout "${configuredLayoutName}": ${
        error instanceof Error ? error.message : String(error)
      }`
    );
    return modifications;
  }
}

/**
 * apply_edits: finalize + apply a PresentationModification, returning the
 * ApplyResultSummary. Optionally returns fresh layout JSON and/or screenshots
 * of the affected slides via `return:{json,image}` so the agent can verify.
 */
export async function executeApplyEditsTool(
  call: ToolCall,
  options: AgentToolRunOptions
): Promise<AgentToolRunResult> {
  const ctx = options.agentToolContext;
  const preslinkBlockReason = getPreslinkTurnBlockReason(ctx);
  if (preslinkBlockReason) {
    return {
      result: {
        tool: "apply_edits",
        success: false,
        error: preslinkBlockReason,
      },
    };
  }
  const emitProgress = (message: string): void => {
    options.onProgress?.(message);
    ctx?.onProgress?.(message);
  };
  const modifications = call.modifications;
  if (!modifications || typeof modifications !== "object") {
    return {
      result: {
        tool: "apply_edits",
        success: false,
        error: "apply_edits requires a 'modifications' object (a PresentationModification).",
      },
    };
  }

  const goldenArtifactRun = ctx?.goldenArtifactRun ?? null;
  const goldenSubdirectory = goldenArtifactRun
    ? (options.goldenSubdirectory ??
      ctx?.nextGoldenToolSubdirectory?.("apply_edits") ??
      ctx?.nextGoldenArtifactSubdirectory?.() ??
      null)
    : null;
  const shouldCaptureApplyPptx =
    Boolean(goldenArtifactRun && goldenSubdirectory) &&
    isGoldenApplyEditsPptxCaptureEnabled() &&
    shouldUseFactSetBridge();
  const captureApplyPptx = async (label: "before" | "after"): Promise<void> => {
    if (!shouldCaptureApplyPptx || !goldenArtifactRun || !goldenSubdirectory) {
      return;
    }
    await runGoldenCaptureStep(
      goldenArtifactRun,
      ctx?.log ?? (() => {}),
      `${goldenSubdirectory}/${label}.pptx`,
      async () => {
        const pptxBase64 = await exportPresentationAsBase64ViaIntents({
          presentationRef: ctx?.presentationRef ?? undefined,
          log: ctx?.log,
        });
        await uploadGoldenArtifactPptx(goldenArtifactRun.runId, label, pptxBase64, {
          subdirectory: goldenSubdirectory,
        });
      }
    );
  };
  if (goldenSubdirectory && goldenArtifactRun) {
    await runGoldenCaptureStep(
      goldenArtifactRun,
      ctx?.log ?? (() => {}),
      `${goldenSubdirectory}/operations.json`,
      async () => {
        const operationsSnapshot = JSON.parse(JSON.stringify(modifications));
        await uploadGoldenArtifactJson(
          goldenArtifactRun.runId,
          "operations",
          operationsSnapshot,
          { subdirectory: goldenSubdirectory }
        );
      }
    );
  }

  const expandedModifications = expandLogoCompanyNames(
    modifications,
    ctx,
    ctx?.log ?? options.onProgress
  );
  const modificationsWithBrandLayout = await applyBrandContentLayoutFallback(
    expandedModifications,
    options
  );
  const finalizedRaw = finalizeModelModifications(modificationsWithBrandLayout);
  // Code-enforced skip-only guard: after a PresLink refresh, never re-touch the
  // refreshed (PresLink-managed) shapes, even if the model targets them.
  const preslinkExclusion = excludePreslinkManagedShapeMods(
    finalizedRaw,
    ctx?.preslinkExcludedShapeIdsBySlideId
  );
  const finalized = preslinkExclusion.modifications;
  if (preslinkExclusion.skippedShapeIds.length > 0) {
    ctx?.log?.(
      `[apply_edits] skipped ${preslinkExclusion.skippedShapeIds.length} edit(s) targeting PresLink-managed shape(s): ${preslinkExclusion.skippedShapeIds.join(", ")}`
    );
  }
  if (preslinkExclusion.skippedSlideOps.length > 0) {
    const skippedSlideOps = preslinkExclusion.skippedSlideOps
      .map((op) => `${op.action}:${op.slideId}`)
      .join(", ");
    ctx?.log?.(
      `[apply_edits] skipped ${preslinkExclusion.skippedSlideOps.length} slide-level structural edit(s) targeting PresLink-managed slide(s): ${skippedSlideOps}`
    );
  }
  const targetSlideCount = finalized.slides?.length ?? 0;
  emitProgress(
    targetSlideCount > 0
      ? `Preparing edits for ${targetSlideCount} slide${targetSlideCount === 1 ? "" : "s"}.`
      : "Preparing slide edits."
  );
  const hasCreatedSlideActions = (finalized.slides || []).some(
    (slide) => slide.action === "insert" || slide.action === "duplicate"
  );
  const preSlideIds = new Set<string>();
  if (hasCreatedSlideActions) {
    emitProgress("Checking current slide order before inserting new slides.");
    try {
      const preSlides = await listSlides({
        forceRefresh: true,
        presentationRef: ctx?.presentationRef ?? undefined,
      });
      for (const slide of preSlides) preSlideIds.add(slide.id);
    } catch (error) {
      ctx?.log?.(
        `[apply_edits] failed to snapshot slides before insert/duplicate: ${
          error instanceof Error ? error.message : String(error)
        }`
      );
    }
  }

  await captureApplyPptx("before");
  let summary: ApplyResultSummary;
  try {
    emitProgress("Applying edits in PowerPoint.");
    summary = await observeToolSubstep({
      langfuseContext: options.langfuseContext,
      name: "tool:apply_edits.host_mutation",
      input: {
        requestedSlides: finalized.slides?.length ?? 0,
        hasCreatedSlideActions,
      },
      metadata: { tool: "apply_edits", step: "host_mutation" },
      run: () =>
        applyModifications(finalized, ctx?.log, undefined, {
          presentationRef: ctx?.presentationRef ?? undefined,
          preferredBounds: ctx?.slideBounds ?? undefined,
          brandId: options.toolPolicy?.brandId ?? undefined,
          onPreparedIntentBatch:
            goldenSubdirectory && goldenArtifactRun
              ? async (preparedOperations) => {
                  await runGoldenCaptureStep(
                    goldenArtifactRun,
                    ctx?.log ?? (() => {}),
                    `${goldenSubdirectory}/prepared_batch_operations.json`,
                    async () => {
                      const preparedOperationsSnapshot = JSON.parse(
                        JSON.stringify(preparedOperations)
                      );
                      await uploadGoldenArtifactJson(
                        goldenArtifactRun.runId,
                        "prepared_batch_operations",
                        preparedOperationsSnapshot,
                        { subdirectory: goldenSubdirectory }
                      );
                    }
                  );
                }
              : undefined,
        }),
      summarizeOutput: (applySummary) => summarizeApplyResult(applySummary),
    });
  } catch (error) {
    return {
      result: {
        tool: "apply_edits",
        success: false,
        error: error instanceof Error ? error.message : String(error),
      },
    };
  } finally {
    await captureApplyPptx("after");
  }

  const data: Record<string, unknown> = { summary: summarizeApplyResult(summary) };
  if (preslinkExclusion.skippedShapeIds.length > 0) {
    data.preslinkExcludedShapes = {
      count: preslinkExclusion.skippedShapeIds.length,
      shapeIds: preslinkExclusion.skippedShapeIds,
      note: "Edits targeting PresLink-managed shapes were skipped; those shapes are owned by the PresLink refresh.",
    };
  }
  if (preslinkExclusion.skippedSlideOps.length > 0) {
    data.preslinkExcludedSlides = {
      count: preslinkExclusion.skippedSlideOps.length,
      slideOps: preslinkExclusion.skippedSlideOps,
      note: "Slide-level structural edits targeting PresLink-managed slides were skipped; those slides contain objects owned by the PresLink refresh.",
    };
  }
  emitProgress("Reading applied edit results.");
  // Surface the model's own natural-language edit summary (modifications.summary)
  // so the UI can show it alongside the structured "Applied X edits across Y
  // slides" count.
  const modelSummary = finalized.summary ?? modifications.summary;
  if (typeof modelSummary === "string" && modelSummary.trim().length > 0) {
    data.modelSummary = modelSummary.trim();
  }
  const slideSummaries = (finalized.slides || [])
    .map((slide) => (typeof slide.summary === "string" ? slide.summary.trim() : ""))
    .filter((summaryText) => summaryText.length > 0);
  if (slideSummaries.length > 0) {
    data.slideSummaries = slideSummaries;
  }
  const affected = collectAffectedSlideIndices(finalized);
  const requestedInsertedCount = (finalized.slides || []).filter(
    (slide) => slide.action === "insert" || slide.action === "duplicate"
  ).length;
  // Best-effort citation capture for task-grounded deck builds: surface the
  // model-declared per-slide citationIds + the declared cited values so the
  // orchestrator can attach SlideCitationsView messages after the build.
  // Real-id-keyed citation bindings from the apply pipeline's manifest (no
  // createdSlides diff / positional pairing). The pool (`citations`) +
  // declared cited values stay so the turn collector / citation pool keep working.
  const citationBindings = summary.citationManifest?.bindings ?? [];
  const declaredCitedValues = buildDeclaredCitedValues(finalized.citations);
  if (citationBindings.length > 0 || (finalized.citations || []).length > 0) {
    data.appliedCitations = {
      bindings: citationBindings,
      citations: finalized.citations || [],
      citedValuesByUuid: declaredCitedValues,
    };
  }
  if (summary.citationManifest) {
    data.citationAttachment = {
      boundSlides: citationBindings.length,
      declaredCitationIds: summary.citationManifest.allCitationIds.length,
    };
  }
  const referenceImages: ReferenceSlideImage[] = [];
  const capturedImageIndices = new Set<number>();

  let createdSlides: Array<{ id: string; index: number; slideNumber?: number }> = [];
  if (hasCreatedSlideActions && preSlideIds.size > 0) {
    try {
      emitProgress("Resolving newly created slides.");
      const postSlides = await listSlides({
        forceRefresh: true,
        presentationRef: ctx?.presentationRef ?? undefined,
      });
      createdSlides = postSlides
        .filter((slide) => !preSlideIds.has(slide.id))
        .map((slide) => ({
          id: slide.id,
          index: slide.index,
          ...(typeof slide.slideNumber === "number" ? { slideNumber: slide.slideNumber } : {}),
        }));
      if (createdSlides.length > 0) {
        data.createdSlides = createdSlides;
        try {
          data.slidesInfo = await observeToolSubstep({
            langfuseContext: options.langfuseContext,
            name: "tool:apply_edits.slide_extraction",
            input: {
              mode: "created_slides",
              createdSlideCount: createdSlides.length,
            },
            metadata: { tool: "apply_edits", step: "slide_extraction" },
            run: () => reExtractSlides({ slides: createdSlides }, ctx),
            summarizeOutput: (slidesInfo) => ({
              slideCount: Array.isArray(slidesInfo?.slides) ? slidesInfo.slides.length : 0,
            }),
          });
        } catch (error) {
          data.slidesInfoError = error instanceof Error ? error.message : String(error);
        }
        const capturedCreatedSlideNumbers: number[] = [];
        for (const slide of createdSlides.slice(0, MAX_AGENT_SLIDE_IMAGES)) {
          if (capturedImageIndices.has(slide.index)) continue;
          try {
            emitProgress(`Capturing preview for slide ${slide.slideNumber ?? slide.index + 1}.`);
            const image = await exportSlideAsImage(slide.index, {
              presentationRef: ctx?.presentationRef ?? undefined,
            });
            referenceImages.push({ slideId: slide.id, image });
            capturedImageIndices.add(slide.index);
            capturedCreatedSlideNumbers.push(slide.slideNumber ?? slide.index);
          } catch (error) {
            ctx?.log?.(
              `[apply_edits] screenshot failed for created slide index ${slide.index}: ${
                error instanceof Error ? error.message : String(error)
              }`
            );
          }
        }
        data.capturedCreatedSlideNumbers = capturedCreatedSlideNumbers;
      }
    } catch (error) {
      data.createdSlidesError = error instanceof Error ? error.message : String(error);
    }
  }

  // Report real-id-keyed citation bindings to the deck-build sink. The manifest
  // already resolved insert/duplicate ops to their real host id, so this no
  // longer depends on the createdSlides diff.
  if (citationBindings.length > 0) {
    ctx?.reportCitationBindings?.({
      bindings: citationBindings,
      citedValuesByUuid: declaredCitedValues,
    });
  }

  if (call.returnImage && affected.length > 0) {
    const refsByIndex = await buildCurrentSlideIndexToRefMap(ctx);
    for (const idx of affected.slice(0, MAX_AGENT_SLIDE_IMAGES)) {
      if (capturedImageIndices.has(idx)) continue;
      try {
        emitProgress(
          `Capturing preview for slide ${refsByIndex.get(idx)?.slideNumber ?? idx + 1}.`
        );
        const image = await exportSlideAsImage(idx, {
          presentationRef: ctx?.presentationRef ?? undefined,
        });
        referenceImages.push({
          slideId: refsByIndex.get(idx)?.id ?? fallbackSlideImageId("index", idx),
          image,
        });
        capturedImageIndices.add(idx);
      } catch (error) {
        ctx?.log?.(
          `[apply_edits] screenshot failed for slide index ${idx}: ${
            error instanceof Error ? error.message : String(error)
          }`
        );
      }
    }
  }

  if (call.returnJson && !data.slidesInfo) {
    try {
      emitProgress("Refreshing slide layout after the edit.");
      data.slidesInfo = await observeToolSubstep({
        langfuseContext: options.langfuseContext,
        name: "tool:apply_edits.slide_extraction",
        input: {
          mode: "window_refresh",
        },
        metadata: { tool: "apply_edits", step: "slide_extraction" },
        run: () => reExtractSlides(undefined, ctx),
        summarizeOutput: (slidesInfo) => ({
          slideCount: Array.isArray(slidesInfo?.slides) ? slidesInfo.slides.length : 0,
        }),
      });
    } catch (error) {
      data.slidesInfoError = error instanceof Error ? error.message : String(error);
    }
  }

  const success = summary.errors.count === 0;
  const cleanApply = success && summary.skipped.count === 0;
  if (cleanApply) {
    emitProgress("Slide edits applied.");
    // Report structured progress only for clean applies. Failed/partial calls
    // can leave confusing deck state behind, but the progress UI should not
    // materialize them as successful slide creation.
    const insertedCount =
      hasCreatedSlideActions && createdSlides.length > 0
        ? createdSlides.length
        : requestedInsertedCount;
    ctx?.reportApplyProgress?.({
      affectedSlideIndices: affected,
      insertedCount,
      created: createdSlides.map((slide) => ({ id: slide.id, index: slide.index })),
      modifiedIds: collectModifiedSlideIds(finalized),
    });
  }
  return {
    result: {
      tool: "apply_edits",
      success,
      data,
      error: success ? undefined : `apply_edits completed with ${summary.errors.count} error(s).`,
    },
    referenceImages: referenceImages.length > 0 ? referenceImages : undefined,
  };
}

/**
 * get_slides_info: observe slides. `returnJson` (default true) re-extracts the
 * true current layout for the requested slides; `returnImage` captures
 * screenshots. Used for layout/overlap/consistency verification.
 */
export async function executeGetSlidesInfoTool(
  call: ToolCall,
  options: AgentToolRunOptions
): Promise<AgentToolRunResult> {
  const ctx = options.agentToolContext;
  const wantImage = call.returnImage === true;
  const wantJson = call.returnJson !== false; // default to JSON when unspecified
  const slideNumbers = (call.slide_numbers || [])
    .map((n) => Math.floor(n))
    .filter((n) => Number.isFinite(n) && n >= 0);

  const data: Record<string, unknown> = { requestedSlideNumbers: slideNumbers };
  const referenceImages: ReferenceSlideImage[] = [];

  if (wantImage && slideNumbers.length > 0) {
    const numberToRef = await buildCurrentSlideNumberToRefMap(ctx);
    const captured: number[] = [];
    for (const slideNumber of slideNumbers.slice(0, MAX_AGENT_SLIDE_IMAGES)) {
      const ref = numberToRef.get(slideNumber);
      const idx = ref?.index ?? fallbackSlideNumberToIndex(slideNumber);
      try {
        const image = await exportSlideAsImage(idx, {
          presentationRef: ctx?.presentationRef ?? undefined,
        });
        referenceImages.push({
          slideId: ref?.id ?? fallbackSlideImageId("slideNumber", slideNumber),
          image,
        });
        captured.push(slideNumber);
      } catch (error) {
        ctx?.log?.(
          `[get_slides_info] screenshot failed for slide ${slideNumber}: ${
            error instanceof Error ? error.message : String(error)
          }`
        );
      }
    }
    data.capturedSlideNumbers = captured;
  }

  if (wantJson) {
    try {
      data.slidesInfo = await observeToolSubstep({
        langfuseContext: options.langfuseContext,
        name: "tool:get_slides_info.slide_extraction",
        input: {
          requestedSlideNumbers: slideNumbers,
        },
        metadata: { tool: "get_slides_info", step: "slide_extraction" },
        run: () => reExtractSlides({ slideNumbers }, ctx),
        summarizeOutput: (slidesInfo) => ({
          slideCount: Array.isArray(slidesInfo?.slides) ? slidesInfo.slides.length : 0,
        }),
      });
    } catch (error) {
      data.slidesInfoError = error instanceof Error ? error.message : String(error);
    }
  }

  if (call.returnLayouts) {
    try {
      const catalog = await extractDeckLayoutCatalogViaIntents(ctx?.log, {
        forceRefresh: true,
        presentationRef: ctx?.presentationRef ?? undefined,
      });
      data.deckLayoutCatalog =
        call.layout_names && call.layout_names.length > 0
          ? {
              layouts: catalog.layouts.filter((layout) =>
                call.layout_names!.some(
                  (name) => name === layout.layoutName || name === layout.matchingName
                )
              ),
            }
          : catalog;
    } catch (error) {
      data.deckLayoutCatalogError = error instanceof Error ? error.message : String(error);
    }
  }

  if (call.returnDesigns) {
    try {
      data.designMetadata = await extractDesignMetadataViaIntents(ctx?.log, {
        forceRefresh: true,
        presentationRef: ctx?.presentationRef ?? undefined,
      });
    } catch (error) {
      data.designMetadataError = error instanceof Error ? error.message : String(error);
    }
  }

  if (call.returnMasterShapes) {
    data.masterShapesNote =
      "Use layouts/designs detail for selected custom layouts first; master-shape extraction is intentionally not returned by default.";
  }

  const attemptedImageCapture = wantImage && slideNumbers.length > 0;
  const success =
    (attemptedImageCapture ? referenceImages.length > 0 : true) && !data.slidesInfoError;
  return {
    result: {
      tool: "get_slides_info",
      success,
      data,
      error: success ? undefined : "get_slides_info could not produce the requested information.",
    },
    referenceImages: referenceImages.length > 0 ? referenceImages : undefined,
  };
}

/**
 * insert_library_slide: insert a pre-built slide-library slide (PPTX) into the
 * deck after `afterSlideId`, then return the created slide's id/index, the
 * re-extracted JSON, and a screenshot so the agent can FILL it by real shape id
 * on a follow-up apply_edits. The library slide's PPTX sources are resolved
 * from the turn context (librarySlideSourcesById) keyed by the plan's
 * librarySlideId, so the agent never handles URLs and no extra network lookup
 * is needed. FactSet-bridge only.
 */
export async function executeInsertLibrarySlideTool(
  call: ToolCall,
  options: AgentToolRunOptions
): Promise<AgentToolRunResult> {
  const ctx = options.agentToolContext;
  const librarySlideId = typeof call.librarySlideId === "string" ? call.librarySlideId.trim() : "";
  if (!librarySlideId) {
    return {
      result: {
        tool: "insert_library_slide",
        success: false,
        error: "insert_library_slide requires a 'library_slide_id'.",
      },
    };
  }

  const source = await resolveLibrarySlidePptxSources(librarySlideId, ctx);
  if (!source || source.pptxSources.length === 0) {
    return {
      result: {
        tool: "insert_library_slide",
        success: false,
        error: `Unknown library_slide_id "${librarySlideId}" (no PPTX source available). Browse the library, then retry, or fall back to apply_edits action:"insert" with an appropriate layout.`,
      },
    };
  }

  let insertion;
  try {
    insertion = await insertSlideLibraryItemsViaFactSet({
      items: [{ id: librarySlideId, title: source.title, pptxSources: source.pptxSources }],
      log: ctx?.log,
      presentationRef: ctx?.presentationRef ?? undefined,
      initialTargetSlideId: call.afterSlideId,
    });
  } catch (error) {
    return {
      result: {
        tool: "insert_library_slide",
        success: false,
        error: error instanceof Error ? error.message : String(error),
      },
    };
  }

  const insertedSlides = insertion.successes.flatMap((entry) => entry.insertedSlides);
  if (insertedSlides.length === 0) {
    const failure = insertion.failures[0]?.error;
    return {
      result: {
        tool: "insert_library_slide",
        success: false,
        error: failure
          ? `Library slide insert failed: ${failure}`
          : "Library slide insert produced no slides.",
      },
    };
  }

  const resolvedRefsById = await resolveCurrentSlideRefsById(
    insertedSlides.map((ref) => ref.insertedSlideId),
    ctx
  );
  const createdSlides = insertedSlides.map((ref) => {
    const resolved = resolvedRefsById.get(ref.insertedSlideId);
    if (resolved) return resolved;

    ctx?.log?.(
      `[insert_library_slide] using insertion-service index for ${ref.insertedSlideId}; fresh slide lookup did not return this id.`
    );
    return {
      id: ref.insertedSlideId,
      ...(typeof ref.insertedSlideIndex === "number"
        ? { index: Math.floor(ref.insertedSlideIndex) }
        : undefined),
    };
  });
  const affectedIndices = createdSlides
    .map((ref) => ref.index)
    .filter((idx): idx is number => typeof idx === "number");
  ctx?.reportApplyProgress?.({
    affectedSlideIndices: affectedIndices,
    insertedCount: insertedSlides.length,
    created: createdSlides.map((slide) => ({
      id: slide.id,
      index: typeof slide.index === "number" ? slide.index : 0,
    })),
  });

  const data: Record<string, unknown> = { createdSlides };
  try {
    data.slidesInfo = await reExtractSlides(
      createdSlides.length > 0 ? { slides: createdSlides } : undefined,
      ctx
    );
  } catch (error) {
    data.slidesInfoError = error instanceof Error ? error.message : String(error);
  }

  const referenceImages: ReferenceSlideImage[] = [];
  const createdSlidesByIndex = new Map<number, CurrentSlideRef>(
    createdSlides
      .filter((slide): slide is CurrentSlideRef => typeof slide.index === "number")
      .map((slide) => [slide.index, slide])
  );
  for (const idx of affectedIndices.slice(0, MAX_AGENT_SLIDE_IMAGES)) {
    try {
      const image = await exportSlideAsImage(idx, {
        presentationRef: ctx?.presentationRef ?? undefined,
      });
      referenceImages.push({
        slideId: createdSlidesByIndex.get(idx)?.id ?? fallbackSlideImageId("index", idx),
        image,
      });
    } catch (error) {
      ctx?.log?.(
        `[insert_library_slide] screenshot failed for slide index ${idx}: ${
          error instanceof Error ? error.message : String(error)
        }`
      );
    }
  }

  return {
    result: {
      tool: "insert_library_slide",
      success: true,
      data,
    },
    referenceImages: referenceImages.length > 0 ? referenceImages : undefined,
  };
}

// ── apply_preslink ────────────────────────────────────────────────────
// Resolve the PresLink-linked source file(s) behind one or more shapes and
// return model-facing metadata (file names, sizes, per-shape mapping) plus a
// bounded, best-effort per-sheet cell preview parsed from each linked workbook.
// Raw base64 bytes are intentionally never returned to the model.

const PRESLINK_MAX_PREVIEW_BASE64_LEN = 12_000_000; // ~9 MB workbook cap for inline parse
const PRESLINK_MAX_PREVIEW_SHEETS = 5;
const PRESLINK_MAX_PREVIEW_CSV_CHARS_PER_SHEET = 20_000;
const PRESLINK_MAX_PREVIEW_CSV_CHARS_TOTAL = 60_000;
const PRESLINK_CSV_TRUNCATION_SUFFIX = "\n...[truncated]";

interface MinimalWorksheet {
  [cellAddress: string]: unknown;
}
interface MinimalWorkbook {
  SheetNames: string[];
  Sheets: Record<string, MinimalWorksheet>;
}
interface MinimalXlsx {
  /** Parse a workbook from either base64 text or unzipped bytes. */
  read(data: string | Uint8Array, options: { type: "base64" | "array" }): MinimalWorkbook;
  utils: {
    /** Convert a worksheet into a compact CSV preview for model observation. */
    sheet_to_csv(worksheet: MinimalWorksheet, options?: { blankrows?: boolean }): string;
  };
}
interface MinimalZipEntry {
  /** Read a zipped workbook entry as bytes for xlsx parsing. */
  async(type: "uint8array"): Promise<Uint8Array>;
}
interface MinimalZip {
  files: Record<string, MinimalZipEntry>;
}
interface MinimalJSZip {
  /** Load the linked archive returned by the PresLink bridge. */
  loadAsync(data: string, options: { base64: boolean }): Promise<MinimalZip>;
}

interface PreslinkSheetPreview {
  name: string;
  csvPreview: string;
}
interface PreslinkWorkbookPreview {
  sheetNames: string[];
  sheets: PreslinkSheetPreview[];
}

/** Extract a display file name from a PresLink path while tolerating slash variants. */
function preslinkFileNameFromPath(filePath: string): string {
  const normalized = filePath.trim();
  return normalized.split(/[\\/]/).filter(Boolean).pop() ?? normalized;
}

/** Attempt workbook parsing and convert parser failures into a nullable result. */
function safeReadWorkbook(read: () => MinimalWorkbook): MinimalWorkbook | null {
  try {
    return read();
  } catch {
    return null;
  }
}

/** Bound a workbook sheet CSV preview and mark truncation for the model. */
function truncatePreslinkCsvPreview(csv: string, maxChars: number): string {
  if (maxChars <= 0) return "";
  if (csv.length <= maxChars) return csv;
  if (maxChars <= PRESLINK_CSV_TRUNCATION_SUFFIX.length) {
    return PRESLINK_CSV_TRUNCATION_SUFFIX.slice(0, maxChars);
  }
  return `${csv.slice(0, maxChars - PRESLINK_CSV_TRUNCATION_SUFFIX.length)}${PRESLINK_CSV_TRUNCATION_SUFFIX}`;
}

/**
 * Read linked file bytes as either a raw workbook or an outer zip that contains
 * a workbook entry.
 */
async function readWorkbookFromBase64(base64: string, XLSX: MinimalXlsx): Promise<MinimalWorkbook> {
  const direct = safeReadWorkbook(() => XLSX.read(base64, { type: "base64" }));
  if (direct && direct.SheetNames.length > 0) return direct;

  const JSZip =
    (JSZipModule as unknown as { default?: MinimalJSZip }).default ??
    (JSZipModule as unknown as MinimalJSZip);
  const zip = await JSZip.loadAsync(base64, { base64: true });
  const spreadsheetEntryName = Object.keys(zip.files).find((name) =>
    /\.(xlsx|xlsm|xlsb|xls|csv)$/i.test(name)
  );
  if (!spreadsheetEntryName) {
    throw new Error("no spreadsheet entry found in linked archive");
  }
  const bytes = await zip.files[spreadsheetEntryName].async("uint8array");
  return XLSX.read(bytes, { type: "array" });
}

/** Build a bounded, best-effort workbook preview from linked PresLink bytes. */
async function tryBuildPreslinkWorkbookPreview(
  base64: string,
  log?: (message: string) => void
): Promise<{ ok: true; data: PreslinkWorkbookPreview } | { ok: false; error: string }> {
  if (base64.length > PRESLINK_MAX_PREVIEW_BASE64_LEN) {
    return { ok: false, error: "linked workbook too large to preview inline" };
  }
  try {
    const XLSX =
      (XLSXModule as unknown as { default?: MinimalXlsx }).default ??
      (XLSXModule as unknown as MinimalXlsx);
    const workbook = await readWorkbookFromBase64(base64, XLSX);

    const previewSheetNames = workbook.SheetNames.slice(0, PRESLINK_MAX_PREVIEW_SHEETS);
    const sheets: PreslinkSheetPreview[] = [];
    let remainingCsvChars = PRESLINK_MAX_PREVIEW_CSV_CHARS_TOTAL;
    for (const name of previewSheetNames) {
      if (remainingCsvChars <= 0) break;
      const worksheet = workbook.Sheets[name];
      if (!worksheet) continue;
      const csv = XLSX.utils.sheet_to_csv(worksheet, {
        blankrows: false,
      });
      const boundedCsv = truncatePreslinkCsvPreview(
        csv,
        Math.min(PRESLINK_MAX_PREVIEW_CSV_CHARS_PER_SHEET, remainingCsvChars)
      );
      sheets.push({ name, csvPreview: boundedCsv });
      remainingCsvChars -= boundedCsv.length;
    }
    return { ok: true, data: { sheetNames: workbook.SheetNames, sheets } };
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    log?.(`[apply_preslink] workbook preview failed: ${message}`);
    return { ok: false, error: `could not parse linked workbook: ${message}` };
  }
}

/** Convert the lightweight file-path intent response into the apply_preslink payload shape. */
function buildPreslinkPathOnlyData(
  shapeRefs: Array<{ slideId: string; id: string }>,
  pathsResult: GetPreslinkFilePathsResult
): Record<string, unknown> {
  const linkedFileByPath = new Map<string, Record<string, unknown>>();
  const linkedFiles: Record<string, unknown>[] = [];
  const shapes = pathsResult.slides.flatMap((slide) =>
    slide.shapes.map((shape) => {
      const files = shape.files.map((file) => {
        const existing = linkedFileByPath.get(file.filePath);
        if (existing) {
          return existing;
        }
        const entry = {
          filePath: file.filePath,
          fileName: preslinkFileNameFromPath(file.filePath),
        };
        linkedFileByPath.set(file.filePath, entry);
        linkedFiles.push(entry);
        return entry;
      });
      return {
        slideId: slide.slideId,
        shapeId: shape.shapeId,
        files,
      };
    })
  );

  const data: Record<string, unknown> = {
    requestedShapes: shapeRefs.map((ref) => ({ slideId: ref.slideId, shapeId: ref.id })),
    presentation: {},
    linkedFiles,
    shapes,
  };
  if (linkedFiles.length === 0) {
    data.note =
      "No PresLink-linked files were found for the requested shape(s). The shape(s) may not be PresLink-linked.";
  }
  return data;
}

/** Build a successful empty-link payload for shapes that are not PresLink-linked. */
function buildPreslinkNoLinksData(
  shapeRefs: Array<{ slideId: string; id: string }>
): Record<string, unknown> {
  return {
    requestedShapes: shapeRefs.map((ref) => ({ slideId: ref.slideId, shapeId: ref.id })),
    presentation: {},
    linkedFiles: [],
    shapes: shapeRefs.map((ref) => ({
      slideId: ref.slideId,
      shapeId: ref.id,
      files: [],
    })),
    note: "No PresLink-linked files were found for the requested shape(s). The shape(s) may not be PresLink-linked.",
  };
}

/** Distinguish host "no PresLink links" failures from documented FactSet errors. */
function isGenericPreslinkFetchFailure(error: unknown): boolean {
  const message = (error instanceof Error ? error.message : String(error)).trim();
  if (!message) return false;
  // Preserve documented factset.error failures as hard tool errors.
  if (/FETCH_PRESLINK_FILES_FAILED/i.test(message)) return false;
  // Host wording can vary by prefix/suffix/casing; match by phrase.
  return /failed to fetch preslink files/i.test(message);
}

/**
 * Identify host-generic get-path failures that carry no actionable code/context.
 * Preserve documented FactSet-coded failures as hard errors.
 */
function isGenericPreslinkPathProbeFailure(error: unknown): boolean {
  const message = (error instanceof Error ? error.message : String(error)).trim();
  if (!message) return false;
  if (/GET_PRESLINK_FILE_PATHS_FAILED/i.test(message)) return false;
  return /failed to get preslink file paths/i.test(message);
}

/**
 * The host rejects a whole path-probe batch with "Shape 'X' on slide 'Y' has no
 * PresLink tags." when any ref in it is unlinked. For a single-ref probe this is
 * a definitive "not linked" answer, not a failure.
 */
function isUnlinkedPreslinkShapeProbeFailure(error: unknown): boolean {
  const message = (error instanceof Error ? error.message : String(error)).trim();
  return /has no preslink tags/i.test(message);
}

/** Remove duplicate and incomplete slide/shape refs, preserving first-seen order. */
function dedupePreslinkRefs(
  refs: Array<{ slideId: string; id: string }>
): Array<{ slideId: string; id: string }> {
  const seen = new Set<string>();
  const deduped: Array<{ slideId: string; id: string }> = [];
  for (const ref of refs) {
    if (!ref.slideId || !ref.id) continue;
    const key = `${ref.slideId}\u0000${ref.id}`;
    if (seen.has(key)) continue;
    seen.add(key);
    deduped.push(ref);
  }
  return deduped;
}

/**
 * Collect every valid shape ref for host-authoritative PresLink probing.
 *
 * Do not filter by PowerPoint shape type here. PresLink metadata is owned by
 * the host, and linked text/geometric shapes are valid even though they are
 * less common than charts and tables.
 */
export function collectPotentialPreslinkShapeRefs(
  content: PresentationContent
): Array<{ slideId: string; id: string }> {
  return dedupePreslinkRefs(
    content.slides.flatMap((slide) =>
      slide.shapes.map((shape) => ({ slideId: slide.id, id: shape.id }))
    )
  );
}

const PRESLINK_PATH_PROBE_BATCH_SIZE = 50;

/**
 * Probe the FactSet intent bridge for the given shape refs and return only those
 * that actually resolve to one or more PresLink-linked files.
 */
export async function collectLinkedPreslinkRefsFromPathsProbe(options: {
  shapeRefs: Array<{ slideId: string; id: string }>;
  ctx?: AgentToolContext;
}): Promise<Array<{ slideId: string; id: string }>> {
  const requestedRefs = dedupePreslinkRefs(options.shapeRefs);
  const requestedKeys = new Set(
    requestedRefs.map((ref) => `${ref.slideId}\u0000${ref.id}`)
  );
  const linkedRefs: Array<{ slideId: string; id: string }> = [];
  const collectLinkedRefsFromPathsResult = (pathsResult: GetPreslinkFilePathsResult): void => {
    for (const slide of pathsResult.slides) {
      for (const shape of slide.shapes) {
        const key = `${slide.slideId}\u0000${shape.shapeId}`;
        if (shape.files.length > 0 && requestedKeys.has(key)) {
          linkedRefs.push({ slideId: slide.slideId, id: shape.shapeId });
        }
      }
    }
  };

  for (let start = 0; start < requestedRefs.length; start += PRESLINK_PATH_PROBE_BATCH_SIZE) {
    const batch = requestedRefs.slice(start, start + PRESLINK_PATH_PROBE_BATCH_SIZE);
    try {
      const pathsResult = await getPreslinkFilePathsViaIntents({
        shapeRefs: batch,
        presentationRef: options.ctx?.presentationRef ?? undefined,
        log: options.ctx?.log,
      });
      collectLinkedRefsFromPathsResult(pathsResult);
      continue;
    } catch (batchError) {
      if (
        !isGenericPreslinkPathProbeFailure(batchError) &&
        !isUnlinkedPreslinkShapeProbeFailure(batchError)
      ) {
        throw batchError;
      }

      // A mixed shape batch fails as a whole when it contains unlinked or
      // unsupported refs. Fallback to per-shape probes so one bad ref does not
      // block discovery of linked shapes on the same slide.

      let successfulSingleProbes = 0;
      let genericSingleFailures = 0;
      for (const ref of batch) {
        try {
          const singleResult = await getPreslinkFilePathsViaIntents({
            shapeRefs: [ref],
            presentationRef: options.ctx?.presentationRef ?? undefined,
            log: options.ctx?.log,
          });
          successfulSingleProbes += 1;
          collectLinkedRefsFromPathsResult(singleResult);
        } catch (singleError) {
          if (isUnlinkedPreslinkShapeProbeFailure(singleError)) {
            continue;
          }
          if (!isGenericPreslinkPathProbeFailure(singleError)) {
            throw singleError;
          }
          genericSingleFailures += 1;
        }
      }

      // If every per-shape probe failed with the same generic message, preserve
      // strict safety and fail closed (likely host/service unavailability).
      if (successfulSingleProbes === 0 && genericSingleFailures === batch.length) {
        throw batchError;
      }
    }
  }
  return dedupePreslinkRefs(linkedRefs);
}

/**
 * Stamp `preslinkLinked` onto the shapes the host confirmed are PresLink-linked
 * so the model can see, in the slide JSON it reads, that those values come from
 * a refresh rather than from research.
 *
 * Only marks positives. A shape missing the marker is "not confirmed", never
 * "confirmed unlinked", because the probe may have been skipped or failed.
 */
export function applyPreslinkLinkedMarkers(
  content: PresentationContent,
  linkedRefs: Array<{ slideId: string; id: string }>
): PresentationContent {
  const linkedIdsBySlideId = new Map<string, Set<string>>();
  for (const ref of linkedRefs) {
    if (!ref.slideId || !ref.id) continue;
    const existing = linkedIdsBySlideId.get(ref.slideId);
    if (existing) {
      existing.add(ref.id);
    } else {
      linkedIdsBySlideId.set(ref.slideId, new Set([ref.id]));
    }
  }
  if (linkedIdsBySlideId.size === 0) return content;

  let contentChanged = false;
  const slides = content.slides.map((slide) => {
    const linkedIds = linkedIdsBySlideId.get(slide.id);
    if (!linkedIds) return slide;
    let slideChanged = false;
    const shapes = slide.shapes.map((shape) => {
      if (!linkedIds.has(shape.id) || shape.preslinkLinked === true) return shape;
      slideChanged = true;
      return { ...shape, preslinkLinked: true as const };
    });
    if (!slideChanged) return slide;
    contentChanged = true;
    return { ...slide, shapes };
  });

  return contentChanged ? { ...content, slides } : content;
}

/**
 * Start the turn-setup linkage probe for the selected slide.
 *
 * Unlike the mutation-path probes, this one is advisory: it exists so the model
 * can SEE which shapes are linked, so it never rejects and never blocks a turn.
 * A host that cannot answer simply yields no markers, which is how the turn
 * behaved before the probe existed. Returns eagerly so the caller can overlap
 * the round-trip with other setup work.
 */
export function probeSelectedSlidePreslinkRefs(options: {
  content: PresentationContent;
  selectedSlide?: SlideContent | null;
  enabled: boolean;
  ctx?: AgentToolContext;
}): Promise<Array<{ slideId: string; id: string }>> {
  const { content, selectedSlide, enabled, ctx } = options;
  if (!enabled || !selectedSlide) return Promise.resolve([]);
  const shapeRefs = collectPotentialPreslinkShapeRefs({
    ...content,
    slides: [selectedSlide],
  });
  if (shapeRefs.length === 0) return Promise.resolve([]);
  return collectLinkedPreslinkRefsFromPathsProbe({ shapeRefs, ctx }).catch((probeError) => {
    ctx?.log?.(`⚠️ Could not determine PresLink-linked shapes: ${probeError}`);
    return [];
  });
}

/**
 * Discover every PresLink-linked shape across the whole deck by extracting the
 * presentation content and batch-probing every shape via the intent bridge.
 * Discovery is strict for mutation safety: extraction/probe failures propagate,
 * and an empty result means the host definitively found no linked objects.
 */
export async function resolveFullDeckPreslinkShapeRefs(options: {
  ctx?: AgentToolContext;
}): Promise<Array<{ slideId: string; id: string }>> {
  const content = await extractPresentationContent(options.ctx?.log, {
    forceRefresh: true,
    presentationRef: options.ctx?.presentationRef ?? undefined,
  });
  if (!content || !Array.isArray(content.slides)) {
    throw new Error("Full-deck PresLink discovery returned no slide content.");
  }

  const candidateRefs = collectPotentialPreslinkShapeRefs(content);
  return collectLinkedPreslinkRefsFromPathsProbe({
    shapeRefs: candidateRefs,
    ctx: options.ctx,
  });
}

/** Resolve and retain an explicit host presentation reference for a PresLink replacement. */
async function resolvePreslinkTargetPresentationRef(
  ctx?: AgentToolContext
): Promise<FactSetPresentationRef> {
  const resolved = ctx?.presentationRef ?? (await getActivePresentationRefViaIntents(ctx?.log));
  if (!resolved?.openPresentationId) {
    throw new Error("Unable to resolve the active presentation for PresLink slide replacement.");
  }
  if (ctx) {
    ctx.presentationRef = resolved;
  }
  return resolved;
}

/** Return a fresh, index-ordered slide snapshot for the targeted presentation. */
async function listPreslinkTargetSlides(
  presentationRef: FactSetPresentationRef,
  ctx?: AgentToolContext
): Promise<FactSetSlideInfo[]> {
  return (
    await listSlidesViaIntents(ctx?.log, {
      forceRefresh: true,
      presentationRef,
    })
  ).sort((left, right) => left.index - right.index);
}

interface PreslinkInsertedSlideCleanup {
  removedCount: number;
  error?: string;
}

async function cleanupPreslinkInsertedSlideIds(options: {
  slideIds: readonly string[];
  presentationRef: FactSetPresentationRef;
  ctx?: AgentToolContext;
}): Promise<PreslinkInsertedSlideCleanup> {
  const slideIds = Array.from(new Set(options.slideIds.map((slideId) => slideId.trim()).filter(Boolean)));
  if (slideIds.length === 0) return { removedCount: 0 };
  try {
    await deleteSlidesViaIntents(slideIds, {
      log: options.ctx?.log,
      presentationRef: options.presentationRef,
    });
    return { removedCount: slideIds.length };
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    options.ctx?.log?.(`[apply_preslink] failed to clean up inserted slides: ${message}`);
    return { removedCount: 0, error: message };
  }
}

/** Best-effort cleanup used when source-formatted insertion is partial or reports an error. */
async function cleanupPreslinkInsertedSlides(options: {
  originalSlideIds: ReadonlySet<string>;
  presentationRef: FactSetPresentationRef;
  ctx?: AgentToolContext;
}): Promise<PreslinkInsertedSlideCleanup> {
  try {
    const currentSlides = await listPreslinkTargetSlides(options.presentationRef, options.ctx);
    const insertedIds = currentSlides
      .filter((slide) => !options.originalSlideIds.has(slide.slideId))
      .map((slide) => slide.slideId);
    if (insertedIds.length === 0) {
      return { removedCount: 0 };
    }
    await deleteSlidesViaIntents(insertedIds, {
      log: options.ctx?.log,
      presentationRef: options.presentationRef,
    });
    return { removedCount: insertedIds.length };
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    options.ctx?.log?.(`[apply_preslink] failed to clean up inserted slides: ${message}`);
    return { removedCount: 0, error: message };
  }
}

type PreslinkSlideDeleteRecovery =
  | { status: "committed" }
  | { status: "not_committed"; insertedSlideIdsPresent: string[] }
  | { status: "ambiguous"; message: string };

const PRESLINK_DELETE_RECOVERY_ATTEMPTS = 3;
const PRESLINK_DELETE_RECOVERY_DELAY_MS = 100;

/**
 * Determine whether a failed delete actually committed before deciding whether
 * removing the refreshed slides is safe. A lost response can leave the host in
 * the committed state, so the error alone is not sufficient evidence that the
 * original slides still exist.
 */
async function reconcilePreslinkSlideDeleteFailure(options: {
  originalSlideIds: readonly string[];
  insertedSlides: readonly FactSetSlideInfo[];
  presentationRef: FactSetPresentationRef;
  ctx?: AgentToolContext;
}): Promise<PreslinkSlideDeleteRecovery> {
  const originalSlideIdSet = new Set(options.originalSlideIds);
  const insertedSlideIds = options.insertedSlides.map((slide) => slide.slideId);
  const insertedSlideIdSet = new Set(insertedSlideIds);
  let lastState:
    | {
        originalSlideIdsPresent: string[];
        insertedSlideIdsPresent: string[];
      }
    | undefined;
  let lastQueryError: string | undefined;
  let lastQuerySucceeded = false;

  for (let attempt = 0; attempt < PRESLINK_DELETE_RECOVERY_ATTEMPTS; attempt += 1) {
    try {
      const currentSlides = await listPreslinkTargetSlides(options.presentationRef, options.ctx);
      const currentSlideIds = new Set(currentSlides.map((slide) => slide.slideId));
      const originalSlideIdsPresent = options.originalSlideIds.filter((slideId) =>
        currentSlideIds.has(slideId)
      );
      const insertedSlideIdsPresent = insertedSlideIds.filter((slideId) =>
        currentSlideIds.has(slideId)
      );
      lastState = { originalSlideIdsPresent, insertedSlideIdsPresent };
      lastQuerySucceeded = true;

      if (
        originalSlideIdsPresent.length === 0 &&
        insertedSlideIdsPresent.length === insertedSlideIdSet.size
      ) {
        return { status: "committed" };
      }
    } catch (error) {
      lastQuerySucceeded = false;
      lastQueryError = error instanceof Error ? error.message : String(error);
    }

    if (attempt < PRESLINK_DELETE_RECOVERY_ATTEMPTS - 1) {
      await new Promise<void>((resolve) => {
        setTimeout(resolve, PRESLINK_DELETE_RECOVERY_DELAY_MS);
      });
    }
  }

  if (!lastState || !lastQuerySucceeded) {
    return {
      status: "ambiguous",
      message: lastQueryError
        ? `Unable to verify the slide state after original slide deletion failed: ${lastQueryError}`
        : "No slide state was available after original slide deletion failed.",
    };
  }

  if (lastState.originalSlideIdsPresent.length === originalSlideIdSet.size) {
    return {
      status: "not_committed",
      insertedSlideIdsPresent: lastState.insertedSlideIdsPresent,
    };
  }

  return {
    status: "ambiguous",
    message:
      `The slide state after original slide deletion failed is ambiguous: ` +
      `${lastState.originalSlideIdsPresent.length}/${originalSlideIdSet.size} original slide(s) ` +
      `and ${lastState.insertedSlideIdsPresent.length}/${insertedSlideIdSet.size} refreshed slide(s) are present.`,
  };
}

interface PreslinkSlideReplacementOptions {
  processedPresentationBase64: string;
  targetSlideIds?: string[];
  /**
   * Saves the refreshed workbooks once every processed slide is in, calling
   * `onSaved` per file, and returns how to find each processed shape's file.
   */
  writeWorkbooks: (
    onSaved: (workbook: PreslinkBindingWorkbook) => void
  ) => Promise<PreslinkRefreshWorkbookResolver>;
  ctx?: AgentToolContext;
  emitProgress: (message: string) => void;
}

/**
 * Replace only the slide set sent to PresLink after the processed deck inserted
 * completely. Originals stay intact on insertion failure or partial insertion.
 */
async function replacePreslinkSlidesFromBase64(
  options: PreslinkSlideReplacementOptions
): Promise<{ insertedSlides: FactSetSlideInfo[]; replacedCount: number }> {
  const presentationRef = await resolvePreslinkTargetPresentationRef(options.ctx);
  // Mark the insert/verify/delete window so the active-slide poll in `useTabs`
  // suppresses PowerPoint's transient replacement selection (instead of opening
  // an empty tab over the in-flight chat). The flag is scoped to this deck and
  // released on success, error, or partial-insert cleanup so ordinary
  // cross-slide navigation during normal chat turns is never blocked.
  const endInDeckSlideReplacement = beginInDeckSlideReplacement(presentationRef.openPresentationId);
  try {
    return await performPreslinkSlideReplacement(options, presentationRef);
  } finally {
    endInDeckSlideReplacement();
  }
}

async function performPreslinkSlideReplacement(
  options: PreslinkSlideReplacementOptions,
  presentationRef: FactSetPresentationRef
): Promise<{ insertedSlides: FactSetSlideInfo[]; replacedCount: number }> {
  const activeSlidesBeforeInsert = await listPreslinkTargetSlides(presentationRef, options.ctx);
  const activeSlidePositionById = new Map(
    activeSlidesBeforeInsert.map((slide, position) => [slide.slideId, position] as const)
  );
  const requestedTargetSlideIds = new Set(
    (options.targetSlideIds ?? [])
      .map((slideId) => String(slideId || "").trim())
      .filter((slideId) => slideId.length > 0)
  );
  const originalSlidesToReplace =
    requestedTargetSlideIds.size > 0
      ? activeSlidesBeforeInsert.filter((slide) => requestedTargetSlideIds.has(slide.slideId))
      : activeSlidesBeforeInsert;
  const missingTargetSlideIds = Array.from(requestedTargetSlideIds).filter(
    (slideId) => !originalSlidesToReplace.some((slide) => slide.slideId === slideId)
  );
  const nonContiguousTargetOffset =
    requestedTargetSlideIds.size > 0
      ? originalSlidesToReplace.findIndex((slide, position, slides) => {
          if (position === 0) return false;
          const previous = slides[position - 1];
          if (!previous) return false;
          const previousPosition = activeSlidePositionById.get(previous.slideId);
          const currentPosition = activeSlidePositionById.get(slide.slideId);
          return (
            typeof previousPosition !== "number" ||
            typeof currentPosition !== "number" ||
            currentPosition !== previousPosition + 1
          );
        })
      : -1;
  const previousNonContiguousTarget =
    nonContiguousTargetOffset > 0 ? originalSlidesToReplace[nonContiguousTargetOffset - 1] : null;
  const firstNonContiguousTarget =
    nonContiguousTargetOffset > 0 ? originalSlidesToReplace[nonContiguousTargetOffset] : null;
  // #region agent log
  logPreslinkDebugToTerminal(() => ({
    hypothesisId: "H12,H13,H14,H15",
    location: "agentEditTools.ts:1518",
    message: "preslink original slide snapshot",
    data: {
      originalCount: originalSlidesToReplace.length,
      activeSlideCount: activeSlidesBeforeInsert.length,
      requestedTargetSlideCount: requestedTargetSlideIds.size,
      missingTargetSlideIds,
      targetSlidesContiguous: nonContiguousTargetOffset === -1,
      nonContiguousTargetGap:
        previousNonContiguousTarget && firstNonContiguousTarget
          ? {
              previousSlideId: previousNonContiguousTarget.slideId,
              previousIndex: previousNonContiguousTarget.index,
              nextSlideId: firstNonContiguousTarget.slideId,
              nextIndex: firstNonContiguousTarget.index,
            }
          : undefined,
      originalSlides: originalSlidesToReplace.map((slide) => ({
        slideId: slide.slideId,
        index: slide.index,
      })),
    },
  }));
  // #endregion
  if (missingTargetSlideIds.length > 0) {
    throw new Error(
      `Unable to resolve ${missingTargetSlideIds.length} PresLink target slide(s) in the active presentation.`
    );
  }
  if (nonContiguousTargetOffset > 0) {
    const detail =
      previousNonContiguousTarget && firstNonContiguousTarget
        ? ` Found a gap between slide IDs "${previousNonContiguousTarget.slideId}" and "${firstNonContiguousTarget.slideId}".`
        : "";
    throw new Error(
      `PresLink target slides must be a single contiguous range in the active presentation.${detail}`
    );
  }
  if (originalSlidesToReplace.length === 0) {
    throw new Error("The active presentation has no PresLink target slides to replace.");
  }
  const targetSlideId = originalSlidesToReplace[originalSlidesToReplace.length - 1]?.slideId;
  if (!targetSlideId) {
    throw new Error("Unable to resolve the final PresLink target slide for insertion.");
  }
  const slideIdsBeforeInsert = new Set(activeSlidesBeforeInsert.map((slide) => slide.slideId));

  options.emitProgress("PresLink: Inserting refreshed slides with source formatting.");
  try {
    await insertSlidesFromBase64WithSourceFormattingViaIntents({
      base64Slides: options.processedPresentationBase64,
      targetSlideId,
      presentationRef,
      log: options.ctx?.log,
      onDebug: (event) => {
        // #region agent log
        logPreslinkDebugToTerminal(() => ({
          hypothesisId: "H9,H10,H11",
          location: "agentEditTools.ts:1533",
          message: `insertSlidesFromBase64WithSourceFormatting ${event.status}`,
          data: {
            error: event.error,
            insertedCount: event.insertedCount,
            resultPreview: event.resultPreview,
            targetSlideId,
          },
        }));
        // #endregion
      },
    });
  } catch (error) {
    const cleanup = await cleanupPreslinkInsertedSlides({
      originalSlideIds: slideIdsBeforeInsert,
      presentationRef,
      ctx: options.ctx,
    });
    const message = error instanceof Error ? error.message : String(error);
    const cleanupSuffix = cleanup.error
      ? ` Cleanup of partially inserted slides also failed: ${cleanup.error}`
      : cleanup.removedCount > 0
        ? ` Removed ${cleanup.removedCount} partially inserted slide(s).`
        : "";
    throw new Error(`${message}${cleanupSuffix}`);
  }

  let slidesAfterInsert: FactSetSlideInfo[];
  try {
    slidesAfterInsert = await listPreslinkTargetSlides(presentationRef, options.ctx);
    // #region agent log
    logPreslinkDebugToTerminal(() => ({
      hypothesisId: "H12",
      location: "agentEditTools.ts:1579",
      message: "preslink post-insert slide snapshot",
      data: {
        slideCount: slidesAfterInsert.length,
        slides: slidesAfterInsert.map((slide) => ({
          slideId: slide.slideId,
          index: slide.index,
        })),
      },
    }));
    // #endregion
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    // #region agent log
    logPreslinkDebugToTerminal(() => ({
      hypothesisId: "H12",
      location: "agentEditTools.ts:1595",
      message: "preslink post-insert slide snapshot failed",
      data: { error: message },
    }));
    // #endregion
    throw error;
  }
  const insertedSlides = slidesAfterInsert.filter(
    (slide) => !slideIdsBeforeInsert.has(slide.slideId)
  );
  // #region agent log
  logPreslinkDebugToTerminal(() => ({
    hypothesisId: "H13",
    location: "agentEditTools.ts:1606",
    message: "preslink inserted slide count evaluated",
    data: {
      expectedInsertedCount: originalSlidesToReplace.length,
      actualInsertedCount: insertedSlides.length,
      insertedSlides: insertedSlides.map((slide) => ({
        slideId: slide.slideId,
        index: slide.index,
      })),
    },
  }));
  // #endregion
  if (insertedSlides.length !== originalSlidesToReplace.length) {
    const cleanup = await cleanupPreslinkInsertedSlides({
      originalSlideIds: slideIdsBeforeInsert,
      presentationRef,
      ctx: options.ctx,
    });
    // #region agent log
    logPreslinkDebugToTerminal(() => ({
      hypothesisId: "H13,H15",
      location: "agentEditTools.ts:1624",
      message: "preslink incomplete insert cleanup completed",
      data: {
        removedCount: cleanup.removedCount,
        error: cleanup.error,
      },
    }));
    // #endregion
    const cleanupSuffix = cleanup.error
      ? ` Cleanup also failed: ${cleanup.error}`
      : ` Removed ${cleanup.removedCount} partially inserted slide(s).`;
    throw new Error(
      `PresLink inserted an incomplete slide set (expected ${originalSlidesToReplace.length}, received ${insertedSlides.length}).${cleanupSuffix}`
    );
  }

  const insertedSlideIds = insertedSlides.map((slide) => slide.slideId);
  const originalSlideIdByInsertedSlideId = new Map(
    [...insertedSlides]
      .sort((left, right) => left.index - right.index)
      .map((slide, position) => [slide.slideId, originalSlidesToReplace[position]?.slideId] as const)
  );
  // The host has no delete-file intent, so workbooks saved before a failure stay on disk.
  const savedWorkbooks: PreslinkBindingWorkbook[] = [];
  const removeInsertedSlides = async (slideIds: readonly string[]): Promise<string> => {
    const cleanup = await cleanupPreslinkInsertedSlideIds({
      slideIds,
      presentationRef,
      ctx: options.ctx,
    });
    if (savedWorkbooks.length > 0) {
      options.ctx?.log?.(
        `⚠️ Refreshed PresLink workbooks left beside the originals: ${savedWorkbooks
          .map((workbook) => workbook.localFileName)
          .join(", ")}`
      );
    }
    return cleanup.error
      ? ` Cleanup of inserted slides also failed: ${cleanup.error}`
      : cleanup.removedCount > 0
        ? ` Removed ${cleanup.removedCount} inserted slide(s).`
        : "";
  };

  let resolveWorkbook: PreslinkRefreshWorkbookResolver;
  try {
    resolveWorkbook = await options.writeWorkbooks((workbook) => savedWorkbooks.push(workbook));
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    throw new Error(`${message}.${await removeInsertedSlides(insertedSlideIds)}`);
  }

  options.emitProgress("PresLink: Rebinding refreshed slide workbooks.");
  try {
    await remapPreslinkBindingsForInsertedSlides({
      insertedSlideIds,
      sourceSlideIdByInsertedSlideId: originalSlideIdByInsertedSlideId,
      resolveWorkbook: (slideId, shape, originalShape) =>
        resolveWorkbook(originalSlideIdByInsertedSlideId.get(slideId), shape, originalShape?.id),
      presentationRef,
      log: options.ctx?.log,
    });
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    throw new Error(
      `PresLink refreshed slide binding verification failed: ${message}.${await removeInsertedSlides(insertedSlideIds)}`
    );
  }

  options.emitProgress("PresLink: Replacing original slides.");
  const originalSlideIdsToDelete = originalSlidesToReplace.map((slide) => slide.slideId);
  // #region agent log
  logPreslinkDebugToTerminal(() => ({
    hypothesisId: "H14",
    location: "agentEditTools.ts:1644",
    message: "preslink original slide delete starting",
    data: {
      deleteCount: originalSlideIdsToDelete.length,
      slideIds: originalSlideIdsToDelete,
    },
  }));
  // #endregion
  try {
    await deleteSlidesViaIntents(originalSlideIdsToDelete, {
      log: options.ctx?.log,
      presentationRef,
    });
    // #region agent log
    logPreslinkDebugToTerminal(() => ({
      hypothesisId: "H14",
      location: "agentEditTools.ts:1659",
      message: "preslink original slide delete succeeded",
      data: { deletedCount: originalSlideIdsToDelete.length },
    }));
    // #endregion
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    const recovery = await reconcilePreslinkSlideDeleteFailure({
      originalSlideIds: originalSlideIdsToDelete,
      insertedSlides,
      presentationRef,
      ctx: options.ctx,
    });
    if (recovery.status === "committed") {
      options.ctx?.log?.(
        "[apply_preslink] original slide deletion reported an error, but the refreshed slide replacement was verified as committed."
      );
      return {
        insertedSlides: insertedSlides.sort((left, right) => left.index - right.index),
        replacedCount: originalSlidesToReplace.length,
      };
    }
    if (recovery.status === "ambiguous") {
      throw new Error(
        `${message} ${recovery.message} Automatic slide rollback was skipped; manual recovery may be necessary.`
      );
    }

    const cleanupSuffix = await removeInsertedSlides(recovery.insertedSlideIdsPresent);
    // #region agent log
    logPreslinkDebugToTerminal(() => ({
      hypothesisId: "H14",
      location: "agentEditTools.ts:1669",
      message: "preslink original slide delete failed",
      data: {
        error: message,
        deleteCount: originalSlideIdsToDelete.length,
        recoveryStatus: recovery.status,
      },
    }));
    // #endregion
    throw new Error(`${message}${cleanupSuffix}`);
  }
  return {
    insertedSlides: insertedSlides.sort((left, right) => left.index - right.index),
    replacedCount: originalSlidesToReplace.length,
  };
}

/**
 * Resolve source paths only when they affect behavior:
 * - always for process/write flows, and
 * - for read-only lookups only when duplicate workbook names could collide.
 */
function shouldResolvePreslinkSourcePaths(options: {
  shouldProcessPresentationFile: boolean;
  linkedFiles: FetchPreslinkFilesResult["linkedFiles"];
}): boolean {
  if (options.linkedFiles.length === 0) return false;
  if (options.shouldProcessPresentationFile) return true;
  const seenFileNames = new Set<string>();
  for (const file of options.linkedFiles) {
    const normalizedFileName = file.fileName?.trim().toLowerCase() || "";
    if (!normalizedFileName) continue;
    if (seenFileNames.has(normalizedFileName)) return true;
    seenFileNames.add(normalizedFileName);
  }
  return false;
}

/**
 * Map each linked workbook's link key to the exact host-resolved file path by
 * cross-referencing intent-bridge file paths with the fetched slide/shape link
 * keys. The processed API filename is not a destination selector.
 */
async function resolvePreslinkSourcePathByLinkKey(options: {
  shapeRefs: Array<{ slideId: string; id: string }>;
  fetched: FetchPreslinkFilesResult;
  ctx?: AgentToolContext;
}): Promise<Map<string, string>> {
  const pathsResult = await getPreslinkFilePathsViaIntents({
    shapeRefs: options.shapeRefs,
    presentationRef: options.ctx?.presentationRef ?? undefined,
    log: options.ctx?.log,
  });
  const linkKeysByShapeKey = new Map<string, string[]>();
  const fileNameByLinkKey = new Map<string, string>();
  for (const slide of options.fetched.slides) {
    for (const shape of slide.shapes) {
      linkKeysByShapeKey.set(`${slide.slideId}\u0000${shape.shapeId}`, shape.linkKeys);
      for (const linkKey of shape.linkKeys) {
        const file = options.fetched.linkedFiles.find((candidate) => candidate.linkKey === linkKey);
        if (file?.fileName) fileNameByLinkKey.set(linkKey, file.fileName);
      }
    }
  }

  const resolvedPathByLinkKey = new Map<string, string>();
  for (const slide of pathsResult.slides) {
    for (const shape of slide.shapes) {
      const shapeKey = `${slide.slideId}\u0000${shape.shapeId}`;
      const linkKeys = linkKeysByShapeKey.get(shapeKey) ?? [];
      if (linkKeys.length === 0) continue;

      const pathsByFileIndex = shape.files.map((file) => file?.filePath?.trim() ?? "");
      const singlePathMatchesAllLinks =
        pathsByFileIndex.length === 1 &&
        linkKeys.every(
          (linkKey) =>
            fileNameByLinkKey.get(linkKey)?.trim().toLowerCase() ===
            fileNameByLinkKey.get(linkKeys[0] ?? "")?.trim().toLowerCase()
        );
      for (let index = 0; index < linkKeys.length; index += 1) {
        const linkKey = linkKeys[index];
        const filePath = pathsByFileIndex[index] || (singlePathMatchesAllLinks ? pathsByFileIndex[0] : "");
        if (filePath && !resolvedPathByLinkKey.has(linkKey)) {
          resolvedPathByLinkKey.set(linkKey, filePath);
        }
      }
    }
  }
  return resolvedPathByLinkKey;
}

type PreslinkRefreshBinding = PreslinkBindingWorkbook | null;

/** A refreshed workbook to save as a new file in its source workbook's folder. */
interface PreslinkRefreshWorkbookPlan {
  sourcePath: string;
  folderPath: string;
  separator: string;
  /** The name PresLink processing returned for the refreshed workbook. */
  fileName: string;
  base64File: string;
  /** Original and processed names of every link to this workbook; a processed tag may carry any of them. */
  aliases: string[];
}

interface PreslinkRefreshSavedWorkbook {
  plan: PreslinkRefreshWorkbookPlan;
  workbook: PreslinkBindingWorkbook;
}

type PreslinkRefreshWorkbookResolver = (
  originalSlideId: string | undefined,
  shape: ShapeContent,
  originalShapeId?: string
) => PreslinkBindingWorkbook;

function splitPreslinkSourcePath(
  sourcePath: string
): { folderPath: string; separator: string } | null {
  const trimmed = sourcePath.trim();
  const separatorIndex = Math.max(trimmed.lastIndexOf("/"), trimmed.lastIndexOf("\\"));
  if (separatorIndex <= 0 || trimmed.includes("\0")) return null;
  return { folderPath: trimmed.slice(0, separatorIndex), separator: trimmed[separatorIndex] };
}

function isPreslinkRefreshWorkbookName(fileName: string): boolean {
  return !fileName.includes("\0") && /.\.(xlsx|xlsm|xlsb|xls|csv)$/i.test(fileName);
}

/** `<stem>_<copy><ext>` for every copy after the first. */
function preslinkRefreshCopyName(fileName: string, copy: number): string {
  if (copy === 1) return fileName;
  const dot = fileName.lastIndexOf(".");
  return `${fileName.slice(0, dot)}_${copy}${fileName.slice(dot)}`;
}

/**
 * Plan one new file per source workbook, named as PresLink processing returned
 * it. Links that share a source path share one file. Throws, before anything
 * is inserted or written, when a name is unusable or when any name the save
 * may try is too long for Excel.
 */
function planPreslinkRefreshWorkbooks(options: {
  fetched: FetchPreslinkFilesResult;
  processedLinkedFiles: readonly PreslinkProcessOutputLinkedFile[];
  sourcePathByLinkKey: ReadonlyMap<string, string>;
}): { plans: PreslinkRefreshWorkbookPlan[]; unresolvedLinkKeys: string[] } {
  const plansByPath = new Map<string, PreslinkRefreshWorkbookPlan>();
  const unresolvedLinkKeys: string[] = [];
  let invalidNameCount = 0;
  for (const file of options.processedLinkedFiles) {
    const sourcePath = options.sourcePathByLinkKey.get(file.link_key) ?? "";
    const location = splitPreslinkSourcePath(sourcePath);
    if (!location) {
      unresolvedLinkKeys.push(file.link_key);
      continue;
    }
    const fileName = preslinkFileNameFromPath(file.file_name ?? "");
    if (!isPreslinkRefreshWorkbookName(fileName)) {
      invalidNameCount += 1;
      continue;
    }
    const pathKey = normalizePreslinkBindingPath(sourcePath);
    const existing = plansByPath.get(pathKey);
    if (existing) {
      existing.aliases.push(fileName);
      continue;
    }
    plansByPath.set(pathKey, {
      sourcePath: sourcePath.trim(),
      ...location,
      fileName,
      base64File: file.zip_base64,
      aliases: [fileName],
    });
  }
  if (invalidNameCount > 0) {
    throw new Error(
      `PresLink processing returned a missing or invalid workbook file name for ${invalidNameCount} linked workbook(s).`
    );
  }
  for (const file of options.fetched.linkedFiles) {
    const sourcePath = options.sourcePathByLinkKey.get(file.linkKey);
    const plan = sourcePath ? plansByPath.get(normalizePreslinkBindingPath(sourcePath)) : undefined;
    if (plan && file.fileName) plan.aliases.push(file.fileName);
  }

  const plans = Array.from(plansByPath.values());
  const tooLong = plans.find(
    (plan) =>
      `${plan.folderPath}${plan.separator}${preslinkRefreshCopyName(
        plan.fileName,
        PRESLINK_WORKBOOK_NAME_ATTEMPTS
      )}`.length > EXCEL_MAX_FULL_PATH_LENGTH
  );
  if (tooLong) {
    throw new Error(
      `Saving the refreshed workbook ${tooLong.fileName} beside its original could make the path longer than Excel's ${EXCEL_MAX_FULL_PATH_LENGTH}-character limit. Move the workbook to a shorter folder path.`
    );
  }
  return { plans, unresolvedLinkKeys };
}

/**
 * Save a refreshed workbook without overwriting anything, under the returned
 * name or one of its `_2` to `_<PRESLINK_WORKBOOK_NAME_ATTEMPTS>` copies. A
 * name the host reports as taken, or one `takenPaths` already holds, moves on
 * to the next copy.
 */
async function writePreslinkRefreshWorkbook(
  plan: PreslinkRefreshWorkbookPlan,
  takenPaths: Set<string>,
  options: { ctx?: AgentToolContext; emitProgress: (message: string) => void }
): Promise<PreslinkBindingWorkbook> {
  for (let copy = 1; copy <= PRESLINK_WORKBOOK_NAME_ATTEMPTS; copy += 1) {
    const localFileName = preslinkRefreshCopyName(plan.fileName, copy);
    const localPath = `${plan.folderPath}${plan.separator}${localFileName}`;
    const pathKey = normalizePreslinkBindingPath(localPath);
    if (takenPaths.has(pathKey)) continue;
    takenPaths.add(pathKey);
    options.emitProgress(`PresLink: Writing ${localFileName} to your local folder.`);
    const outcome = await writeFileFromBase64WithOutcomeViaIntents({
      filePath: localPath,
      base64File: plan.base64File,
      overwrite: false,
      log: options.ctx?.log,
      onDebug: (event) => {
        logPreslinkDebugToTerminal(() => ({
          hypothesisId: "H6,H7,H8",
          location: "agentEditTools.ts:2194",
          message: `writeFileFromBase64 route ${event.status}`,
          data: {
            fileName: localFileName,
            appId: event.appId,
            attempt: event.attempt,
            error: event.error,
            intentName: event.intentName,
          },
        }));
      },
    });
    if (outcome.status === "written") return { localFileName, localPath };
    if (outcome.status === "indeterminate") {
      throw new Error(`the host could not confirm that ${localFileName} was saved: ${outcome.message}`);
    }
    if (!isPreslinkWorkbookWriteCollision(outcome)) {
      throw new Error(`${outcome.code ? `${outcome.code}: ` : ""}${outcome.message}`);
    }
  }
  throw new Error("the names tried are already taken in the workbook's folder");
}

/**
 * Save every planned workbook beside its original, which is never written.
 * `onSaved` hears about each file as it lands, so a later failure can report
 * the files left behind.
 */
async function writePreslinkRefreshWorkbooks(options: {
  plans: readonly PreslinkRefreshWorkbookPlan[];
  ctx?: AgentToolContext;
  emitProgress: (message: string) => void;
  onSaved: (workbook: PreslinkBindingWorkbook) => void;
}): Promise<PreslinkRefreshSavedWorkbook[]> {
  const takenPaths = new Set(
    options.plans.map((plan) => normalizePreslinkBindingPath(plan.sourcePath))
  );
  const saved: PreslinkRefreshSavedWorkbook[] = [];
  for (const plan of options.plans) {
    let workbook: PreslinkBindingWorkbook;
    try {
      workbook = await writePreslinkRefreshWorkbook(plan, takenPaths, options);
    } catch (error) {
      const message = (error instanceof Error ? error.message : String(error)).replace(/\.+$/, "");
      throw new Error(
        `Failed to write processed linked workbook "${plan.fileName}" (${saved.length}/${options.plans.length} written): ${message}`
      );
    }
    options.onSaved(workbook);
    saved.push({ plan, workbook });
    logPreslinkDebugToTerminal(() => ({
      hypothesisId: "H3",
      location: "agentEditTools.ts:2082",
      message: "writeFileFromBase64 intent succeeded",
      data: {
        fileName: workbook.localFileName,
        writtenCount: saved.length,
        totalEntries: options.plans.length,
      },
    }));
  }
  return saved;
}

function normalizePreslinkBindingPath(value: string): string {
  return value.trim().replace(/[\\/]+/g, "/").replace(/\/$/, "").toLowerCase();
}

function preslinkBindingNameKey(fileName: string): string {
  return `name:${preslinkFileNameFromPath(fileName).trim().toLowerCase()}`;
}

function preslinkBindingPathKey(filePath: string): string {
  return `path:${normalizePreslinkBindingPath(filePath)}`;
}

function preslinkBindingFolderKey(folderPath: string): string {
  const normalizedFolder = normalizePreslinkBindingPath(folderPath);
  return normalizedFolder ? `folder:${normalizedFolder}` : "";
}

function joinPreslinkTagPath(path: string, fileName: string): string {
  const normalizedPath = path.trim().replace(/[\\/]+$/, "");
  const normalizedFileName = preslinkFileNameFromPath(fileName);
  if (!normalizedPath) return normalizedFileName;
  const separator = normalizedPath.includes("\\") ? "\\" : "/";
  return `${normalizedPath}${separator}${normalizedFileName}`;
}

/** Point every name and path a processed tag may carry for this workbook at the file saved for it. */
function registerPreslinkRefreshBinding(
  bindings: Map<string, PreslinkRefreshBinding>,
  { plan, workbook: target }: PreslinkRefreshSavedWorkbook
): void {
  const keys = new Set<string>([
    preslinkBindingPathKey(plan.sourcePath),
    preslinkBindingPathKey(target.localPath),
    preslinkBindingNameKey(plan.sourcePath),
    preslinkBindingNameKey(target.localFileName),
    preslinkBindingFolderKey(plan.folderPath),
    ...plan.aliases.flatMap((alias) => {
      const aliasFileName = preslinkFileNameFromPath(alias);
      return aliasFileName
        ? [
            preslinkBindingNameKey(aliasFileName),
            preslinkBindingPathKey(`${plan.folderPath}${plan.separator}${aliasFileName}`),
          ]
        : [];
    }),
  ]);
  for (const key of keys) {
    if (!key || key.endsWith(":")) continue;
    const existing = bindings.get(key);
    if (existing === undefined) {
      bindings.set(key, target);
      continue;
    }
    if (
      existing === null ||
      normalizePreslinkBindingPath(existing.localPath) !==
        normalizePreslinkBindingPath(target.localPath)
    ) {
      bindings.set(key, null);
    }
  }
}

function buildPreslinkRefreshBindings(
  saved: readonly PreslinkRefreshSavedWorkbook[]
): Map<string, PreslinkRefreshBinding> {
  const bindings = new Map<string, PreslinkRefreshBinding>();
  for (const entry of saved) {
    registerPreslinkRefreshBinding(bindings, entry);
  }
  return bindings;
}

/**
 * Look a processed shape up among the workbooks its original slide linked to,
 * then its original shape's, then the whole deck's. Two workbooks in one folder
 * can come back from PresLink processing with the same name, and then only the
 * original shape's link keys tell them apart. The original shape is known only
 * by its position on the slide, so it settles just what the slide cannot.
 */
function buildPreslinkRefreshResolver(options: {
  saved: readonly PreslinkRefreshSavedWorkbook[];
  slides: PreslinkProcessPresentationRequest["data"]["slides"];
  sourcePathByLinkKey: ReadonlyMap<string, string>;
}): PreslinkRefreshWorkbookResolver {
  const deckBindings = buildPreslinkRefreshBindings(options.saved);
  const savedBySourcePath = new Map(
    options.saved.map(
      (entry) => [normalizePreslinkBindingPath(entry.plan.sourcePath), entry] as const
    )
  );
  const bindingsForLinkKeys = (linkKeys: readonly string[]) => {
    const linked = new Set<PreslinkRefreshSavedWorkbook>();
    for (const linkKey of linkKeys) {
      const sourcePath = options.sourcePathByLinkKey.get(linkKey);
      const entry = sourcePath
        ? savedBySourcePath.get(normalizePreslinkBindingPath(sourcePath))
        : undefined;
      if (entry) linked.add(entry);
    }
    return buildPreslinkRefreshBindings(Array.from(linked));
  };
  const slideBindings = new Map<string, Map<string, PreslinkRefreshBinding>>();
  const shapeBindings = new Map<string, Map<string, PreslinkRefreshBinding>>();
  for (const slide of options.slides) {
    slideBindings.set(
      slide.slide_id,
      bindingsForLinkKeys(slide.shapes.flatMap((shape) => shape.link_keys))
    );
    for (const shape of slide.shapes) {
      shapeBindings.set(
        `${slide.slide_id}\u0000${shape.shape_id}`,
        bindingsForLinkKeys(shape.link_keys)
      );
    }
  }
  return (originalSlideId, shape, originalShapeId) =>
    resolvePreslinkRefreshBinding(shape, deckBindings, [
      originalSlideId === undefined ? undefined : slideBindings.get(originalSlideId),
      originalSlideId === undefined || originalShapeId === undefined
        ? undefined
        : shapeBindings.get(`${originalSlideId}\u0000${originalShapeId}`),
    ]);
}

function resolvePreslinkRefreshBinding(
  shape: ShapeContent,
  bindings: ReadonlyMap<string, PreslinkRefreshBinding>,
  narrowerBindings: ReadonlyArray<ReadonlyMap<string, PreslinkRefreshBinding> | undefined> = [],
): PreslinkBindingWorkbook {
  const fields = readPreslinkWorkbookTagFields(shape);
  const fullPathKey = preslinkBindingPathKey(joinPreslinkTagPath(fields.path, fields.fileName));
  const folderKey = preslinkBindingFolderKey(fields.path);
  const nameKey = preslinkBindingNameKey(fields.fileName);
  for (const scoped of narrowerBindings) {
    const hit =
      scoped &&
      (scoped.get(fullPathKey) ||
        (folderKey ? scoped.get(folderKey) : undefined) ||
        scoped.get(nameKey));
    if (hit) return hit;
  }

  const exact = bindings.get(fullPathKey);
  if (exact === null) {
    throw new Error(`PresLink shape ${shape.id} has an ambiguous workbook path.`);
  }
  if (exact) return exact;

  const byFolder = folderKey ? bindings.get(folderKey) : undefined;
  if (byFolder) return byFolder;

  const byName = bindings.get(nameKey);
  if (byName === null) {
    throw new Error(`PresLink shape ${shape.id} has an ambiguous workbook filename.`);
  }
  if (!byName) {
    const uniqueBindings = Array.from(bindings.values())
      .filter((binding): binding is PreslinkBindingWorkbook => binding !== null)
      .filter(
        (binding, index, candidates) =>
          candidates.findIndex(
            (candidate) =>
              normalizePreslinkBindingPath(candidate.localPath) ===
              normalizePreslinkBindingPath(binding.localPath)
          ) === index
      );
    if (uniqueBindings.length === 1) return uniqueBindings[0];
    throw new Error(
      `Could not map PresLink shape ${shape.id} workbook "${fields.fileName}" to a refreshed workbook.`
    );
  }
  return byName;
}

/**
 * Processing requires both explicit agent intent and host capability. Prompt
 * text alone never enables mutation.
 */
function shouldProcessPreslinkPresentationFile(
  call: ToolCall,
  rawCall: { process_presentation_file?: unknown }
): boolean {
  return call.processPresentationFile === true || rawCall.process_presentation_file === true;
}

/**
 * True only when the destructive PresLink pipeline completed through local
 * workbook write-back and slide replacement. Tool success alone is not enough:
 * read-only/no-link calls are also successful tool results.
 */
export function isCompletedPreslinkProcessingResult(result: ToolResult): boolean {
  if (!result.success || !result.data || typeof result.data !== "object") {
    return false;
  }
  const processPresentation = (result.data as Record<string, unknown>).processPresentation;
  if (
    !processPresentation ||
    typeof processPresentation !== "object" ||
    Array.isArray(processPresentation)
  ) {
    return false;
  }
  const processRecord = processPresentation as Record<string, unknown>;
  return (
    processRecord.success === true &&
    processRecord.presentationApplied === true &&
    processRecord.completed === true
  );
}

/**
 * apply_preslink: resolve the PresLink-linked source file(s) behind the
 * given shape(s) via the FactSet intent bridge. Returns per-shape file names and
 * a bounded cell-data preview (never raw base64). FactSet-bridge only.
 */
async function executeFetchPreslinkFilesToolInternal(
  call: ToolCall,
  options: AgentToolRunOptions
): Promise<AgentToolRunResult> {
  const ctx = options.agentToolContext;
  const emitProgress = (message: string): void => {
    options.onProgress?.(message);
    ctx?.onProgress?.(message);
  };
  const handlePreslinkProcessEvent = (event: PreslinkProcessEvent): void => {
    const excelFile = event.excel_file ? ` (${event.excel_file})` : "";
    const message = event.message || event.type;
    ctx?.log?.(`[apply_preslink] ${event.type}${excelFile}: ${message}`);
  };
  if (options.toolPolicy?.allowPreslink !== true) {
    return {
      result: {
        tool: "apply_preslink",
        success: false,
        error: "apply_preslink is unavailable for this request context.",
      },
    };
  }

  // The OpenAI native path parses tool args into `preslinkShapeRefs` ({ slideId,
  // id }); the Anthropic/Gemini providers pass the model's tool-call JSON through
  // generically, so it arrives in the documented schema shape (`shapes` with
  // { slide_id, shape_id }). Accept either, plus snake_case option aliases.
  const rawCall = call as ToolCall & {
    operation?: unknown;
    shapes?: Array<{ slide_id?: unknown; shape_id?: unknown; slideId?: unknown; id?: unknown }>;
    include_ppt_file?: unknown;
    include_cell_data?: unknown;
    process_presentation_file?: unknown;
    scope?: unknown;
  };
  const operation =
    call.preslinkOperation === "refresh" || call.preslinkOperation === "inspect"
      ? call.preslinkOperation
      : rawCall.operation === "refresh" || rawCall.operation === "inspect"
        ? rawCall.operation
        : shouldProcessPreslinkPresentationFile(call, rawCall)
          ? "refresh"
          : "inspect";
  const rawShapeRefs = rawCall.preslinkShapeRefs ?? rawCall.shapes ?? [];
  /** Pick the first non-empty string from native and schema-aliased tool args. */
  const pickString = (...values: unknown[]): string => {
    for (const value of values) {
      if (typeof value === "string" && value.trim().length > 0) return value.trim();
    }
    return "";
  };
  let shapeRefs = dedupePreslinkRefs(
    rawShapeRefs
      .map((ref) => ({
        id: pickString((ref as { id?: unknown })?.id, (ref as { shape_id?: unknown })?.shape_id),
        slideId: pickString(
          (ref as { slideId?: unknown })?.slideId,
          (ref as { slide_id?: unknown })?.slide_id
        ),
      }))
      .filter((ref) => ref.id.length > 0 && ref.slideId.length > 0)
  );
  const requestedShapeRefs = shapeRefs;

  if (shapeRefs.length === 0) {
    return {
      result: {
        tool: "apply_preslink",
        success: false,
        error: "apply_preslink requires at least one shape with a slide_id and shape_id.",
      },
    };
  }

  const includePptFile = call.includePptFile === true || rawCall.include_ppt_file === true;
  // Default: parse cell data unless explicitly disabled (either arg spelling).
  const includeCellData = call.includeCellData !== false && rawCall.include_cell_data !== false;
  const requestedProcessPresentationFile = operation === "refresh";
  // Data-safety gate: destructive processing requires an explicit agent tool
  // argument and an independently enabled FactSet-host capability.
  const preslinkProcessingAllowed = options.toolPolicy?.allowPreslinkProcessing === true;
  if (requestedProcessPresentationFile && !preslinkProcessingAllowed) {
    return {
      result: {
        tool: "apply_preslink",
        success: false,
        error:
          "PresLink refresh is unavailable for this request context; no workbook or slide changes were made.",
      },
    };
  }
  const shouldProcessPresentationFile =
    requestedProcessPresentationFile && preslinkProcessingAllowed;

  if (shouldProcessPresentationFile && ctx?.preslinkProcessCompleted === true) {
    if (ctx.preslinkRefreshFailed === true) {
      return {
        result: {
          tool: "apply_preslink",
          success: false,
          error:
            "PresLink already replaced slides in this turn, but fresh managed-object verification failed. Processing will not be retried.",
        },
      };
    }
    const presentationApplied = ctx.preslinkPresentationApplied === true;
    // #region agent log
    logPreslinkDebugToTerminal(() => ({
      hypothesisId: "H5",
      location: "agentEditTools.ts:1743",
      message: "apply_preslink duplicate processing skipped",
      data: {
        requestedShapeCount: shapeRefs.length,
        includePptFile,
        includeCellData,
        shouldProcessPresentationFile,
        presentationApplied,
      },
    }));
    // #endregion
    return {
      result: {
        tool: "apply_preslink",
        success: true,
        data: {
          processPresentation: {
            success: true,
            presentationApplied,
            completed: true,
            alreadyProcessedThisTurn: true,
            ...(presentationApplied ? {} : { noLinkedShapes: true }),
          },
          note: presentationApplied
            ? "PresLink processing already completed successfully in this turn; duplicate processing was skipped."
            : "PresLink already ran in this turn and found no linked shapes, so no slides were replaced; duplicate processing was skipped.",
        },
      },
    };
  }

  const requestedScope = call.preslinkScope ?? rawCall.scope ?? "shapes";
  if (requestedScope !== "shapes" && requestedScope !== "deck") {
    return {
      result: {
        tool: "apply_preslink",
        success: false,
        error: 'apply_preslink scope must be either "shapes" or "deck".',
      },
    };
  }
  const shouldDiscoverFullDeckRefs = requestedScope === "deck";
  if (shouldDiscoverFullDeckRefs) {
    try {
      shapeRefs = await resolveFullDeckPreslinkShapeRefs({ ctx });
    } catch (error) {
      return {
        result: {
          tool: "apply_preslink",
          success: false,
          error: `PresLink full-deck discovery failed: ${
            error instanceof Error ? error.message : String(error)
          }`,
        },
      };
    }
  } else if (shouldProcessPresentationFile) {
    try {
      shapeRefs = await collectLinkedPreslinkRefsFromPathsProbe({
        shapeRefs,
        ctx,
      });
    } catch (error) {
      return {
        result: {
          tool: "apply_preslink",
          success: false,
          error: `PresLink target discovery failed: ${
            error instanceof Error ? error.message : String(error)
          }`,
        },
      };
    }
  }

  if (shapeRefs.length === 0) {
    if (shouldProcessPresentationFile) {
      if (ctx) {
        ctx.preslinkProcessCompleted = true;
      }
      return {
        result: {
          tool: "apply_preslink",
          success: true,
          data: {
            ...buildPreslinkNoLinksData(requestedShapeRefs),
            operation: "refresh",
            processPresentation: {
              success: true,
              presentationApplied: false,
              completed: true,
              noLinkedShapes: true,
            },
            note:
              "No host-verified PresLink shapes were found in the requested target. No PresLink mutation was needed; continue with any native PowerPoint work in a later tool hop.",
            nextStep:
              "Required: continue the original request against native PowerPoint objects using the current slide ids. Call apply_edits in a later tool hop before finishing.",
          },
        },
      };
    }
    return {
      result: {
        tool: "apply_preslink",
        success: true,
        data: buildPreslinkNoLinksData(requestedShapeRefs),
      },
    };
  }

  // #region agent log
  logPreslinkDebugToTerminal(() => ({
    hypothesisId: "H2,H5",
    location: "agentEditTools.ts:1796",
    message: "apply_preslink scope resolved",
    data: {
      shapeRefCount: shapeRefs.length,
      includePptFile,
      includeCellData,
      shouldProcessPresentationFile,
      requestedScope,
      resolvedFullDeckRefs: shouldDiscoverFullDeckRefs,
    },
  }));
  // #endregion

  if (!includeCellData && !includePptFile && !shouldProcessPresentationFile) {
    try {
      const pathsResult = await getPreslinkFilePathsViaIntents({
        shapeRefs,
        presentationRef: ctx?.presentationRef ?? undefined,
        log: ctx?.log,
      });
      return {
        result: {
          tool: "apply_preslink",
          success: true,
          data: buildPreslinkPathOnlyData(shapeRefs, pathsResult),
        },
      };
    } catch (error) {
      return {
        result: {
          tool: "apply_preslink",
          success: false,
          error: error instanceof Error ? error.message : String(error),
        },
      };
    }
  }

  let fetched: FetchPreslinkFilesResult;
  // Processing requires the presentation archive, even when include_ppt_file is false.
  const fetchIncludePptFile = includePptFile || shouldProcessPresentationFile;
  try {
    fetched = await fetchPreslinkFilesViaIntents({
      shapeRefs,
      includePptFile: fetchIncludePptFile,
      presentationRef: ctx?.presentationRef ?? undefined,
      log: ctx?.log,
    });
  } catch (error) {
    if (isGenericPreslinkFetchFailure(error) && !shouldProcessPresentationFile) {
      const data = buildPreslinkNoLinksData(shapeRefs);
      return {
        result: {
          tool: "apply_preslink",
          success: true,
          data,
        },
      };
    }
    return {
      result: {
        tool: "apply_preslink",
        success: false,
        error: error instanceof Error ? error.message : String(error),
      },
    };
  }

  if (shouldProcessPresentationFile && fetched.linkedFiles.length === 0) {
    return {
      result: {
        tool: "apply_preslink",
        success: false,
        error: "PresLink processing was requested, but the host returned no linked workbooks.",
        data: buildPreslinkNoLinksData(shapeRefs),
      },
    };
  }

  let sourcePathByLinkKey = new Map<string, string>();
  if (
    shouldResolvePreslinkSourcePaths({
      shouldProcessPresentationFile,
      linkedFiles: fetched.linkedFiles,
    })
  ) {
    try {
      sourcePathByLinkKey = await resolvePreslinkSourcePathByLinkKey({
        shapeRefs,
        fetched,
        ctx,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      ctx?.log?.(`[apply_preslink] failed to resolve source paths: ${message}`);
    }
  }

  // #region agent log
  logPreslinkDebugToTerminal(() => ({
    hypothesisId: "H2",
    location: "agentEditTools.ts:1870",
    message: "preslink source path resolution complete",
    data: {
      fetchedLinkedFileCount: fetched.linkedFiles.length,
      resolvedSourcePathCount: sourcePathByLinkKey.size,
      linkedFiles: fetched.linkedFiles.slice(0, 20).map((file) => ({
        fileName: file.fileName,
        linkKeyPreview: file.linkKey.slice(0, 8),
        sourcePathResolved: sourcePathByLinkKey.has(file.linkKey),
      })),
    },
  }));
  // #endregion

  const linkedFileByKey = new Map<string, Record<string, unknown>>();
  const linkedFileByIdentity = new Map<string, Record<string, unknown>>();
  const linkedFiles: Record<string, unknown>[] = [];
  for (const file of fetched.linkedFiles) {
    const normalizedFileName = file.fileName?.trim().toLowerCase() || "";
    const sourcePath = sourcePathByLinkKey.get(file.linkKey)?.trim().toLowerCase() || "";
    const identity = sourcePath
      ? `${sourcePath}\u0000${normalizedFileName || file.linkKey}`
      : normalizedFileName || `link_key:${file.linkKey}`;
    let entry = linkedFileByIdentity.get(identity);
    if (!entry) {
      entry = {
        linkKey: file.linkKey,
        ...(file.fileName ? { fileName: file.fileName } : {}),
        byteLength: base64ByteLength(file.zipBase64),
      };
      if (includeCellData && file.zipBase64) {
        const preview = await tryBuildPreslinkWorkbookPreview(file.zipBase64, ctx?.log);
        if ("data" in preview) {
          entry.cellData = preview.data;
        } else {
          entry.cellDataError = preview.error;
        }
      }
      linkedFileByIdentity.set(identity, entry);
      linkedFiles.push(entry);
    }
    linkedFileByKey.set(file.linkKey, entry);
  }

  const shapes = fetched.slides.flatMap((slide) =>
    slide.shapes.map((shape) => ({
      slideId: slide.slideId,
      shapeId: shape.shapeId,
      files: shape.linkKeys
        .map((linkKey) => linkedFileByKey.get(linkKey))
        .filter((entry): entry is Record<string, unknown> => Boolean(entry))
        .filter((entry, index, entries) => entries.indexOf(entry) === index)
        .map((entry) => ({
          linkKey: entry.linkKey,
          ...(entry.fileName ? { fileName: entry.fileName } : {}),
        })),
    }))
  );

  const presentation: Record<string, unknown> = {
    ...(fetched.presentation.fileName ? { fileName: fetched.presentation.fileName } : {}),
    ...(fetched.presentation.presentationId
      ? { presentationId: fetched.presentation.presentationId }
      : {}),
    ...(fetchIncludePptFile
      ? { archiveByteLength: base64ByteLength(fetched.presentation.zipBase64) }
      : {}),
  };

  const data: Record<string, unknown> = {
    requestedShapes: shapeRefs.map((ref) => ({ slideId: ref.slideId, shapeId: ref.id })),
    presentation,
    linkedFiles,
    shapes,
  };
  const postRefreshReferenceImages: ReferenceSlideImage[] = [];
  if (linkedFiles.length === 0) {
    data.note = buildPreslinkNoLinksData(shapeRefs).note;
  }

  if (shouldProcessPresentationFile && fetched.linkedFiles.length > 0) {
    try {
      const request = await buildPreslinkProcessPresentationRequest(fetched, {
        prompt:
          ctx?.preslinkProcessingPrompt ??
          ctx?.userPrompt,
        skipLlm: false,
        linkedFileIdentityByLinkKey: new Map(
          fetched.linkedFiles.flatMap((file) => {
            const sourcePath = sourcePathByLinkKey.get(file.linkKey);
            return sourcePath
              ? [[file.linkKey, `${sourcePath}\u0000${file.fileName || ""}`] as const]
              : [];
          })
        ),
      });
      const processOptions: PostPreslinkProcessPresentationFileOptions = {
        signal: ctx?.signal,
        onEvent: handlePreslinkProcessEvent,
        onProgress: (message, event) => {
          emitProgress(`PresLink: ${formatPreslinkProgressMessage(message, event)}`);
        },
      };
      let processResult: PreslinkProcessResult;
      try {
        processResult = await postPreslinkProcessPresentationFile(request, processOptions);
      } catch (error) {
        if (!(error instanceof PreslinkReauthRequiredError)) {
          throw error;
        }
        emitProgress("PresLink: Reconnecting to FactSet…");
        await forceReconnectFactSetOAuth();
        processResult = await postPreslinkProcessPresentationFile(request, processOptions);
      }
      const summaryErrors = Array.isArray(processResult.summary?.errors)
        ? processResult.summary.errors
        : [];
      data.processPresentation = {
        ...processResult.summary,
        success: summaryErrors.length === 0,
        presentationApplied: false,
        completed: false,
      };
      if (summaryErrors.length > 0) {
        (data.processPresentation as Record<string, unknown>).presentationApplied = false;
        (data.processPresentation as Record<string, unknown>).applySkippedReason = "process_errors";
        const errorPreview = summaryErrors
          .slice(0, 3)
          .map((error) => {
            if (typeof error === "string") return error;
            if (error && typeof error === "object") {
              const record = error as Record<string, unknown>;
              return String(record.message || record.error || record.code || "unknown error");
            }
            return String(error);
          })
          .join("; ");
        throw new Error(
          `PresLink processing returned ${summaryErrors.length} error(s): ${errorPreview}`
        );
      }
      if (!processResult.processedPresentationBase64) {
        (data.processPresentation as Record<string, unknown>).presentationApplied = false;
        (data.processPresentation as Record<string, unknown>).applySkippedReason =
          "missing_processed_presentation";
        throw new Error("PresLink processing did not return a processed presentation.");
      }
      // Axis refitting ships with Preslink chart creation, behind its flag.
      if (options.toolPolicy?.allowPreslinkChartCreation === true) {
        processResult = await redrawWithFittedValueAxes(
          request,
          processResult,
          processOptions,
          emitProgress,
          ctx?.log
        );
      }

      const processedLinkedFiles = Array.isArray(processResult.processedLinkedFiles)
        ? processResult.processedLinkedFiles
        : [];
      const expectedLinkedFileKeys = new Set(
        request.data.linked_files.map((file) => file.link_key)
      );
      const processedLinkedFileKeys = new Set(processedLinkedFiles.map((file) => file.link_key));
      // #region agent log
      logPreslinkDebugToTerminal(() => ({
        hypothesisId: "H1",
        location: "agentEditTools.ts:2002",
        message: "preslink process result extracted",
        data: {
          summaryErrorCount: summaryErrors.length,
          hasProcessedPresentation: Boolean(processResult.processedPresentationBase64),
          processedPresentationByteLength: base64ByteLength(
            processResult.processedPresentationBase64 ?? ""
          ),
          expectedLinkedFileCount: expectedLinkedFileKeys.size,
          processedLinkedFileCount: processedLinkedFiles.length,
          processedLinkedFiles: processedLinkedFiles.slice(0, 20).map((file) => ({
            fileName: file.file_name,
            linkKeyPreview: file.link_key.slice(0, 8),
            byteLength: base64ByteLength(file.zip_base64),
          })),
        },
      }));
      // #endregion
      const missingLinkedFileKeys = Array.from(expectedLinkedFileKeys).filter(
        (linkKey) => !processedLinkedFileKeys.has(linkKey)
      );
      const unexpectedLinkedFileKeys = Array.from(processedLinkedFileKeys).filter(
        (linkKey) => !expectedLinkedFileKeys.has(linkKey)
      );
      if (
        missingLinkedFileKeys.length > 0 ||
        unexpectedLinkedFileKeys.length > 0 ||
        processedLinkedFiles.length !== expectedLinkedFileKeys.size
      ) {
        throw new Error(
          `PresLink processing returned an incomplete workbook set (expected ${expectedLinkedFileKeys.size}, received ${processedLinkedFiles.length}).`
        );
      }
      // The previews above were parsed before processing, so they still hold
      // pre-refresh values. Re-parse them from the processed bytes; a workbook
      // we cannot re-parse loses its preview rather than reporting stale
      // numbers beside the refreshed slide content.
      if (includeCellData) {
        for (const entry of linkedFiles) {
          delete entry.cellData;
          entry.cellDataError = "refreshed workbook preview unavailable";
        }
        for (const file of processedLinkedFiles) {
          const entry = linkedFileByKey.get(file.link_key);
          if (!entry || !file.zip_base64) continue;
          const preview = await tryBuildPreslinkWorkbookPreview(file.zip_base64, ctx?.log);
          if ("data" in preview) {
            entry.cellData = preview.data;
            delete entry.cellDataError;
          } else {
            entry.cellDataError = preview.error;
          }
        }
      }

      const { plans: workbookPlans, unresolvedLinkKeys } = planPreslinkRefreshWorkbooks({
        fetched,
        processedLinkedFiles,
        sourcePathByLinkKey,
      });
      // #region agent log
      logPreslinkDebugToTerminal(() => ({
        hypothesisId: "H2,H3",
        location: "agentEditTools.ts:2042",
        message: "preslink workbook write plan built",
        data: {
          writeEntryCount: workbookPlans.length,
          unresolvedLinkKeyCount: unresolvedLinkKeys.length,
          writeEntries: workbookPlans.slice(0, 20).map((plan) => ({
            fileName: plan.fileName,
            byteLength: base64ByteLength(plan.base64File),
          })),
          unresolvedLinkKeyPreviews: unresolvedLinkKeys.map((linkKey) => linkKey.slice(0, 8)),
        },
      }));
      // #endregion
      if (unresolvedLinkKeys.length > 0) {
        (data.processPresentation as Record<string, unknown>).writeResult = {
          writtenCount: 0,
          failedCount: 0,
          unresolvedCount: unresolvedLinkKeys.length,
        };
        throw new Error(
          `Unable to resolve the source folder for ${unresolvedLinkKeys.length} processed linked workbook(s).`
        );
      }

      // Insert the processed full deck after the current slide set, verify that
      // every slide arrived, save the refreshed workbooks beside the originals,
      // point the new slides at them, then delete the original slides. This
      // preserves source formatting without opening a separate presentation.
      // #region agent log
      logPreslinkDebugToTerminal(() => ({
        hypothesisId: "H4",
        location: "agentEditTools.ts:2123",
        message: "preslink slide replacement starting",
        data: {
          processedPresentationByteLength: base64ByteLength(
            processResult.processedPresentationBase64
          ),
          processedPresentationFileName: processResult.processedPresentationFileName,
        },
      }));
      // #endregion
      const replacement = await replacePreslinkSlidesFromBase64({
        processedPresentationBase64: processResult.processedPresentationBase64,
        targetSlideIds: request.data.slides.map((slide) => slide.slide_id),
        writeWorkbooks: async (onSaved) => {
          let writtenCount = 0;
          try {
            const saved = await writePreslinkRefreshWorkbooks({
              plans: workbookPlans,
              ctx,
              emitProgress,
              onSaved: (workbook) => {
                writtenCount += 1;
                onSaved(workbook);
              },
            });
            (data.processPresentation as Record<string, unknown>).writeResult = {
              writtenCount,
              failedCount: 0,
              unresolvedCount: 0,
            };
            return buildPreslinkRefreshResolver({
              saved,
              slides: request.data.slides,
              sourcePathByLinkKey,
            });
          } catch (error) {
            (data.processPresentation as Record<string, unknown>).writeResult = {
              writtenCount,
              failedCount: 1,
              unresolvedCount: 0,
            };
            throw error;
          }
        },
        ctx,
        emitProgress,
      });
      // #region agent log
      logPreslinkDebugToTerminal(() => ({
        hypothesisId: "H4",
        location: "agentEditTools.ts:2138",
        message: "preslink slide replacement succeeded",
        data: {
          insertedCount: replacement.insertedSlides.length,
          replacedCount: replacement.replacedCount,
        },
      }));
      // #endregion
      (data.processPresentation as Record<string, unknown>).presentationApplied = true;
      (data.processPresentation as Record<string, unknown>).completed = true;
      (data.processPresentation as Record<string, unknown>).slideReplacement = {
        insertedCount: replacement.insertedSlides.length,
        replacedCount: replacement.replacedCount,
      };
      if (ctx) {
        ctx.preslinkProcessCompleted = true;
        ctx.preslinkPresentationApplied = true;
      }

      emitProgress("PresLink: Re-reading refreshed slides and verifying managed objects.");
      const refreshLifecycle = await finalizePreslinkRefreshLifecycle(
        {
          presentationContent: ctx?.latestPresentationContent ?? options.presentationContent,
          refreshedSlideIds: replacement.insertedSlides.map((slide) => slide.slideId),
          agentToolContext: ctx ?? {},
          extractionFilters: ctx?.preslinkExtractionFilters,
        },
        {
          collectPotentialPreslinkShapeRefs,
          collectLinkedPreslinkRefsFromPathsProbe,
          applyPreslinkLinkedMarkers,
          extractPresentationContent,
          extractWindowedPresentationContent,
        }
      );
      data.slidesInfo = refreshLifecycle.freshScopeContent;
      (data.processPresentation as Record<string, unknown>).refreshedSlideIds =
        refreshLifecycle.freshScopeContent.slides.map((slide) => slide.id);
      (data.processPresentation as Record<string, unknown>).managedShapeCount =
        Array.from(refreshLifecycle.excludedShapeIdsBySlideId.values()).reduce(
          (count, shapeIds) => count + shapeIds.size,
          0
        );

      for (const slide of refreshLifecycle.freshScopeContent.slides.slice(
        0,
        MAX_AGENT_SLIDE_IMAGES
      )) {
        try {
          postRefreshReferenceImages.push({
            slideId: slide.id,
            image: await exportSlideAsImage(slide.index, {
              presentationRef: ctx?.presentationRef ?? undefined,
            }),
          });
        } catch (error) {
          ctx?.log?.(
            `[apply_preslink] failed to capture refreshed slide ${slide.id}: ${
              error instanceof Error ? error.message : String(error)
            }`
          );
        }
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      const existingProcessPresentationData =
        (data.processPresentation as Record<string, unknown> | undefined) ?? {};
      if (ctx && existingProcessPresentationData.presentationApplied === true) {
        ctx.preslinkRefreshFailed = true;
      }
      // #region agent log
      logPreslinkDebugToTerminal(() => ({
        hypothesisId: "H1,H2,H3,H4,H5",
        location: "agentEditTools.ts:2154",
        message: "preslink processing branch failed",
        data: {
          error: message,
          hadProcessPresentationData: Object.keys(existingProcessPresentationData).length > 0,
          presentationApplied:
            existingProcessPresentationData.presentationApplied === true ? true : false,
          applySkippedReason: existingProcessPresentationData.applySkippedReason,
          writeResult: existingProcessPresentationData.writeResult,
        },
      }));
      // #endregion
      data.processPresentation = {
        ...existingProcessPresentationData,
        success: false,
        completed: false,
        presentationApplied:
          existingProcessPresentationData.presentationApplied === true ? true : false,
        error: message,
      };
      return {
        result: {
          tool: "apply_preslink",
          success: false,
          error: `PresLink update failed: ${message}`,
          data,
        },
      };
    }
  }

  if (shouldProcessPresentationFile) {
    data.nextStep =
      "Required: PresLink is complete. Re-evaluate the original request against the fresh slidesInfo and call apply_edits in a later tool hop to update every relevant native text, table, or chart before finishing. Do not edit PresLink-managed shapes.";
  }
  return {
    result: {
      tool: "apply_preslink",
      success: true,
      data,
    },
    ...(postRefreshReferenceImages.length > 0
      ? { referenceImages: postRefreshReferenceImages }
      : {}),
  };
}

function preslinkCallRequestsRefresh(call: ToolCall): boolean {
  const rawCall = call as ToolCall & {
    operation?: unknown;
    process_presentation_file?: unknown;
  };
  return (
    call.preslinkOperation === "refresh" ||
    rawCall.operation === "refresh" ||
    call.processPresentationFile === true ||
    rawCall.process_presentation_file === true
  );
}

/** Execute PresLink and make every failed mutating attempt turn-fatal. */
export async function executeFetchPreslinkFilesTool(
  call: ToolCall,
  options: AgentToolRunOptions
): Promise<AgentToolRunResult> {
  const ctx = options.agentToolContext;
  const mutating = preslinkCallRequestsRefresh(call);
  try {
    const result = await executeFetchPreslinkFilesToolInternal(call, options);
    if (mutating && !result.result.success && ctx) {
      ctx.preslinkMutationFailedReason =
        result.result.error || "PresLink refresh failed without an error message.";
    } else if (mutating && result.result.success && ctx) {
      ctx.preslinkMutationFailedReason = undefined;
    }
    return result;
  } catch (error) {
    if (mutating && ctx) {
      ctx.preslinkMutationFailedReason =
        error instanceof Error ? error.message : String(error || "PresLink refresh failed.");
    }
    throw error;
  }
}
